// @ts-nocheck
import { createSignal, createEffect, onMount, onCleanup, Show, untrack, createMemo, lazy, Suspense, For } from "solid-js";
import { Portal } from "solid-js/web";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import { createStore } from "solid-js/store";

// Using dynamic import for mapboxgl to avoid module resolution issues
let mapboxgl: any = null;

import Loading from "../../utilities/Loading";
import PlayPause from "../../utilities/PlayPause";
import Overlay from "../Overlay";
import MapSettings from "../../menus/MapSettings";
import MapTimeSeries from "./MapTimeSeries";
import { EXPLORE_MAP_COLOR_MODES } from "./mapColorModes";
import MultiMapTimeSeries from "./MultiMapTimeSeries";
import { error as logError, debug as logDebug, warn as logWarn } from "../../../utils/console";
import { waitForPaint } from "../../../utils/waitForRender";
import { registerActiveComponent, unregisterActiveComponent } from "../../../pages/Dashboard";

// New component architecture
import TrackLayer from "../map/components/TrackLayer";
import GlMapTrackLayer from "../map/components/gl/GlMapTrackLayer";
import GlMapMultiTrackLayer from "../map/components/gl/GlMapMultiTrackLayer";
import GlManeuverLayer from "../map/components/gl/GlManeuverLayer";
import GlMapBoatLayer from "../map/components/gl/GlMapBoatLayer";
import BoatLayer from "../map/components/BoatLayer";
import SelectionLayer from "../map/components/SelectionLayer";
import MultiTrackLayer from "../map/components/MultiTrackLayer";
import MultiBoatLayer from "../map/components/MultiBoatLayer";
import TagsExploreMarkersLayer from "../map/components/TagsExploreMarkersLayer";
import AlarmsExploreMarkersLayer from "../map/components/AlarmsExploreMarkersLayer";
import RaceCourseLayer from "../map/components/RaceCourseLayer";
import WindArrow from "../map/components/WindArrow";

// Overlay system
import { initializeOverlays, overlayRegistry } from "./overlays";
import OverlayManager from "./overlays/OverlayManager";
const FleetDataTable = lazy(() => import("../guages/FleetDataTable"));

import { unifiedDataStore } from "../../../store/unifiedDataStore";
import { sourcesStore } from "../../../store/sourcesStore";
import { defaultChannelsStore } from "../../../store/defaultChannelsStore";
import { collectBoatWindSamples, groupRowsBySourceId } from "../../../utils/boatWindSamples";
import { collectBoatHistorySamples } from "./overlays/windField/windSampleHistory";
import { tooltip, setTooltip } from "../../../store/globalStore";
import { persistantStore } from "../../../store/persistantStore";
import { initializeSourceSelections } from "../../../utils/sourceInitialization";
import { apiEndpoints, config } from "@config/env";
import { getData, getTimezoneForDate, postData } from "../../../utils/global";
import { extractFilterOptions, applyDataFilter } from "../../../utils/dataFiltering";

import { 
  triggerUpdate, 
  setTriggerUpdate, 
  selection, 
  selectedEvents, 
  setHasSelection, 
  hasSelection, 
  setSelection, 
  setIsCut, 
  setCutEvents, 
  selectedRange, 
  setSelectedRange, 
  selectedRanges,
  cutEvents,
  selectedDate as selectionSelectedDate, 
  isCut,
  clearSelection
} from "../../../store/selectionStore";
import {
  selectedStatesTimeseries,
  setSelectedStatesTimeseries,
  selectedRacesTimeseries,
  setSelectedRacesTimeseries,
  selectedLegsTimeseries,
  setSelectedLegsTimeseries,
  selectedGradesTimeseries,
  setSelectedGradesTimeseries,
  raceOptions,
  setRaceOptions,
  setIsTrainingHourMode,
  legOptions,
  setLegOptions,
  gradeOptions,
  setGradeOptions,
  setHasChartsWithOwnFilters,
  isTrainingHourMode,
  selectedSources as filterStoreSelectedSources,
  setSelectedSources as filterStoreSetSelectedSources
} from "../../../store/filterStore";
import { showPlayback, selectedTime, setSelectedTime, isPlaying, setIsPlaying, playbackSpeed, timeWindow, setTimeWindow, smoothPlaybackTimeForTrack, syncSelectedTimeManual, startPeriodicSync, setIsManualTimeChange, requestTimeControl, releaseTimeControl, takeTagsSeekMapCenter, stopPlaybackClock } from "../../../store/playbackStore";
import { logPageLoad } from "../../../utils/logging";
import { processMapDataWithWorker } from "../../../utils/workerManager";
import { persistentSettingsService } from "../../../services/persistentSettingsService";
import { user } from "../../../store/userStore";
import { isSameRace } from "../../../utils/raceValueUtils";
import {
  classifyFleetDateRacesResponse,
  fleetTimeWindowWhenRaceFilterApplied,
  shouldHideFleetFullTimeWindowForMode,
  type FleetRaceModeStatus,
} from "../../../utils/fleetTimeWindow";
import { centerMapOnTrackTime } from "../../../utils/mapSeekCenter";
import WaitingModal from "../../utilities/WaitingModal";
import { toastStore } from "../../../store/toastStore";
import { hasProjectWritePermission } from "../../../utils/userPermissions";
import { ASSIGNABLE_GRADES } from "../../../utils/mapGradeSelection";
import { applyMapGrade } from "../../../services/mapGradeEventsService";

import "../../../styles/thirdparty/mapbox-gl.css";

interface MapContainerProps {
  objectName?: string;
  enableTimeWindow?: boolean;
  sourceMode?: string;
  mapFilterScope?: 'full' | 'raceLegOnly';
  dataSourcesOptions?: any[] | (() => any[]);
  /** When true, show Tags explore map markers when the Tags rail is open. */
  exploreUserEventsUi?: boolean;
  [key: string]: any;
}

interface XRange {
  min: number;
  max: number;
}

export default function MapContainer(props: MapContainerProps) {
  // Guard: don't render map until sourcesStore is ready (prevents empty selectedSourceIds in splitscreen/production)
  const [isSourcesReady, setIsSourcesReady] = createSignal(false);
  const [hasShownMapOnce, setHasShownMapOnce] = createSignal(false);
  createEffect(() => {
    if (sourcesStore.isReady()) {
      setIsSourcesReady(true);
    }
  });

  // Register map as active when this component is in the tree.
  createEffect(() => {
    registerActiveComponent('map');
    // onCleanup, not a returned function: Solid discards createEffect return values, so 'map' was
    // never removed from the global activeComponents set (pages/Dashboard.tsx:28-49) and the
    // selection banner stayed suppressed on other pages for the rest of the session. Safe to fire
    // per re-run too — this effect reads nothing tracked, so it only runs once.
    onCleanup(() => unregisterActiveComponent('map'));
  });

  const [mapContainer, setMapContainer] = createSignal<HTMLDivElement | null>(null);
  let chartContainer: HTMLElement | null = null;
  const [map, setMap] = createSignal<mapboxgl.Map | null>(null);
  const [gradeMenu, setGradeMenu] = createSignal<{
    x: number;
    y: number;
    flip: boolean;
    lat: number | null;
    lng: number | null;
    onTrack: boolean;
  } | null>(null);
  const [gradeMenuOpen, setGradeMenuOpen] = createSignal(false);
  const [m1Saving, setM1Saving] = createSignal(false);
  const [gradeApplying, setGradeApplying] = createSignal(false);
  const [gradeJob, setGradeJob] = createSignal<{ jobId: string; processId: string; label: string } | null>(null);
  const [tilesAvailable, setTilesAvailable] = createSignal(true);
  const [currentZoom, setCurrentZoom] = createSignal<number>(14);
  
  // Zoom settings from class object
  const [zoomSettings, setZoomSettings] = createSignal<{
    race_course?: number;
    boats?: number;
    maneuvers?: number;
  } | null>(null);
  
  // Default zoom threshold if zoom_settings object is not found
  const DEFAULT_ZOOM_THRESHOLD = 16;
  
  // Reactive zoom thresholds - these will update when zoomSettings changes
  const boatsZoomThreshold = createMemo(() => {
    const settings = zoomSettings();
    const threshold = (settings && settings.boats !== undefined) ? settings.boats : DEFAULT_ZOOM_THRESHOLD;
    logDebug('MapContainer: boatsZoomThreshold computed', { 
      settings, 
      boatsValue: settings?.boats, 
      threshold, 
      default: DEFAULT_ZOOM_THRESHOLD 
    });
    return threshold;
  });
  
  const maneuversZoomThreshold = createMemo(() => {
    const settings = zoomSettings();
    const threshold = (settings && settings.maneuvers !== undefined) ? settings.maneuvers : DEFAULT_ZOOM_THRESHOLD;
    logDebug('MapContainer: maneuversZoomThreshold computed', { 
      settings, 
      maneuversValue: settings?.maneuvers, 
      threshold, 
      default: DEFAULT_ZOOM_THRESHOLD 
    });
    return threshold;
  });
  
  const raceCourseZoomThreshold = createMemo(() => {
    const settings = zoomSettings();
    const threshold = (settings && settings.race_course !== undefined) ? settings.race_course : DEFAULT_ZOOM_THRESHOLD;
    logDebug('MapContainer: raceCourseZoomThreshold computed', { 
      settings, 
      raceCourseValue: settings?.race_course, 
      threshold, 
      default: DEFAULT_ZOOM_THRESHOLD 
    });
    return threshold;
  });
  
  // Get zoom threshold for a specific feature (for logging/debugging)
  const getZoomThreshold = (feature: 'race_course' | 'boats' | 'maneuvers'): number => {
    if (feature === 'boats') return boatsZoomThreshold();
    if (feature === 'maneuvers') return maneuversZoomThreshold();
    if (feature === 'race_course') return raceCourseZoomThreshold();
    return DEFAULT_ZOOM_THRESHOLD;
  };
  
  // Computed signals for visibility based on zoom level - these are now fully reactive
  // Allow boats to show when zoomed out (zoom <= 12) or when zoomed in past threshold
  const showBoats = createMemo(() => currentZoom() <= 12 || currentZoom() >= boatsZoomThreshold());
  const showManeuvers = createMemo(() => currentZoom() >= maneuversZoomThreshold());
  const showRaceCourse = createMemo(() => currentZoom() >= raceCourseZoomThreshold());
  
  // Legacy computed signal for backward compatibility (uses boats threshold)
  const showManeuversAndBoats = createMemo(() => showBoats() && showManeuvers());

  // Native Mapbox GL rendering (flag-gated migration off D3-SVG overlays). Both paths coexist.
  const nativeMode = createMemo(() => config.MAP_GL_NATIVE === true);

  // GL path only: the track (Mapbox line layers) and the maneuver markers (Canvas2D overlay) are separate
  // components, so the markers used to appear on an empty map while the track was still building. The track
  // layer latches this once its first frame is actually painted; GlManeuverLayer stays blank until then.
  // (The SVG path draws maneuvers inside the track render pass, so it needs no gate.)
  const [glTrackPainted, setGlTrackPainted] = createSignal(false);
  
  // Track zoom changes and log visibility state changes (only when visibility actually changes)
  let lastBoatsVisible = showBoats();
  let lastManeuversVisible = showManeuvers();
  let lastRaceCourseVisible = showRaceCourse();
  
  createEffect(() => {
    const zoom = currentZoom();
    const boatsVisible = showBoats();
    const maneuversVisible = showManeuvers();
    const raceCourseVisible = showRaceCourse();
    
    // Only log when visibility states actually change
    if (boatsVisible !== lastBoatsVisible || 
        maneuversVisible !== lastManeuversVisible || 
        raceCourseVisible !== lastRaceCourseVisible) {
      logDebug('MapContainer: Zoom visibility changed', {
        zoom,
        boatsThreshold: getZoomThreshold('boats'),
        maneuversThreshold: getZoomThreshold('maneuvers'),
        raceCourseThreshold: getZoomThreshold('race_course'),
        boatsVisible,
        maneuversVisible,
        raceCourseVisible,
        boatsChanged: boatsVisible !== lastBoatsVisible,
        maneuversChanged: maneuversVisible !== lastManeuversVisible,
        raceCourseChanged: raceCourseVisible !== lastRaceCourseVisible
      });
      
      lastBoatsVisible = boatsVisible;
      lastManeuversVisible = maneuversVisible;
      lastRaceCourseVisible = raceCourseVisible;
    }
  });
  
  // Get object name from props or use default (e.g. "basics" when opened from explore sidebar)
  const objectName = props?.objectName || 'map_default';
  // Effective overlay: if user has ever applied a choice (including NONE), use stored; else fall back to objectName from sidebar
  const effectiveDataOverlay = (): string => {
    const stored = dataOverlayName();
    const choiceApplied = getStoredOverlayState('dataOverlayChoiceApplied', false);
    if (choiceApplied) return stored ?? '';
    return (stored && stored.trim()) || (sourceMode === 'single' && objectName && objectName !== 'map_default' ? objectName : '');
  };
  const enableTimeWindow = props?.enableTimeWindow || false;
  const sourceMode = props?.sourceMode || 'single';
  const mapFilterScope = props?.mapFilterScope || 'full';

  // Move AbortController inside component scope with better lifecycle management
  let abortController: AbortController = new AbortController();
  let isInitializing = false;
  
  // Flag to prevent brush restoration from triggering brushed function
  let isRestoringBrush = false;
  
  // Flag to prevent infinite loops in handleDataUpdate
  let isUpdatingFromEffect = false;
  
  // Flag to track if filterStore sources have been initialized (prevents repeated initialization)
  let hasInitializedFilterStoreSources = false;
  let lastProjectId: string | undefined = undefined;

  // Track if initial data has been received
  let hasReceivedInitialData = false;

  // Set initial loading state
  unifiedDataStore.setLoading('map', true);
  
  // Fallback timeout to clear loading state if map fails to load
  let loadingTimeout: ReturnType<typeof setTimeout> | null = null;
  loadingTimeout = setTimeout(() => {
    if (unifiedDataStore.getLoading('map')) {
      logError('Map loading timeout - clearing loading state');
      unifiedDataStore.setLoading('map', false);
    }
    loadingTimeout = null;
  }, 30000); // 30 second timeout

  const [xRange, setRange] = createStore<XRange>({ min: 0, max: 100 });
  const [maptypes] = createSignal<string[]>([...EXPLORE_MAP_COLOR_MODES]);
  
  // Use persistantStore.colorType instead of local signal for consistency across pages
  const { colorType: maptype, setColorType: setMaptype } = persistantStore;
  const { selectedClassName, selectedProjectId, selectedDate, selectedSourceId, setSelectedSourceId, setSelectedSourceName } = persistantStore;

  // Use defaultChannelsStore for channel names (automatically updates when class/project changes)
  const { bspName, twsName, twdName, twaName, latName, lngName, hdgName, sogName, cogName, isReady: defaultChannelsReady } = defaultChannelsStore;
  
  // Log when default channels become ready
  createEffect(() => {
    const ready = defaultChannelsReady();
    const channels = defaultChannelsStore.defaultChannels();
    if (ready && channels) {
      logDebug('MapContainer: Default channels ready', {
        bsp: bspName(),
        tws: twsName(),
        twd: twdName(),
        twa: twaName(),
        lat: latName(),
        lng: lngName(),
        hdg: hdgName(),
        allChannels: channels
      });
    }
  });

  // Fetch zoom settings from class object
  createEffect(async () => {
    const className = persistantStore.selectedClassName();
    const projectId = persistantStore.selectedProjectId();
    
    if (!className || !projectId) {
      setZoomSettings(null);
      return;
    }
    
    try {
      // Always fetch from API first to get the latest data (cache might be stale)
      logDebug('MapContainer: Fetching zoom_settings from API', { className, projectId });
      const response = await getData(
        `${apiEndpoints.app.classes}/object?class_name=${encodeURIComponent(className)}&project_id=${projectId}&object_name=zoom_settings`
      );
      
      if (response.success && response.data) {
        logDebug('MapContainer: Loaded zoom_settings from API', {
          rawData: response.data,
          boats: response.data.boats,
          maneuvers: response.data.maneuvers,
          race_course: response.data.race_course
        });
        
        // Handle case where API returns the value directly (not nested)
        let zoomSettingsData: { race_course?: number; boats?: number; maneuvers?: number };
        if (response.data.boats !== undefined || response.data.maneuvers !== undefined || response.data.race_course !== undefined) {
          // Data is already in the expected format
          zoomSettingsData = response.data;
        } else if (response.data.value) {
          // API might return { value: { boats: 12, ... } }
          zoomSettingsData = typeof response.data.value === 'string' ? JSON.parse(response.data.value) : response.data.value;
        } else {
          // Try parsing if it's a string
          zoomSettingsData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        }
        
        logDebug('MapContainer: Parsed zoom_settings', zoomSettingsData);
        setZoomSettings(zoomSettingsData as { race_course?: number; boats?: number; maneuvers?: number });
        
        // Cache it for future use (non-blocking)
        (async () => {
          try {
            const { huniDBStore } = await import("../../../store/huniDBStore");
            await huniDBStore.storeObject(className, 'zoom_settings', zoomSettingsData);
          } catch (cacheError) {
            logWarn('MapContainer: Failed to cache zoom_settings', cacheError);
          }
        })();
      } else {
        logDebug('MapContainer: zoom_settings not found in API, checking cache', { 
          success: response.success, 
          hasData: !!response.data 
        });
        
        // Fallback to cache if API doesn't have it
        const { huniDBStore } = await import("../../../store/huniDBStore");
        const cached = await huniDBStore.getObject(className, 'zoom_settings');
        
        if (cached && typeof cached === 'object') {
          logDebug('MapContainer: Loaded zoom_settings from HuniDB cache (fallback)', cached);
          setZoomSettings(cached as { race_course?: number; boats?: number; maneuvers?: number });
        } else {
          logDebug('MapContainer: zoom_settings not found in cache either, using defaults');
          setZoomSettings(null);
        }
      }
    } catch (error) {
      logWarn('MapContainer: Error fetching zoom_settings, checking cache', error);
      
      // Fallback to cache on error
      try {
        const { huniDBStore } = await import("../../../store/huniDBStore");
        const cached = await huniDBStore.getObject(className, 'zoom_settings');
        
        if (cached && typeof cached === 'object') {
          logDebug('MapContainer: Loaded zoom_settings from HuniDB cache (error fallback)', cached);
          setZoomSettings(cached as { race_course?: number; boats?: number; maneuvers?: number });
        } else {
          logWarn('MapContainer: Error fetching zoom_settings, using defaults', error);
          setZoomSettings(null);
        }
      } catch (cacheError) {
        logWarn('MapContainer: Error fetching zoom_settings and cache check failed, using defaults', error);
        setZoomSettings(null);
      }
    }
  });

  // Multi-mode: data sources (fleet) selection
  const [availableSources, setAvailableSources] = createSignal<Array<{ source_id: number; source_name: string }>>([]); // [{source_id, source_name}]
  
  // Use filterStore for source selection (consistent with PerfSettings)
  // Convert between source names (filterStore) and source IDs (for API calls)
  const selectedSourceIds = createMemo(() => {
    const sourceNames = filterStoreSelectedSources();
    const available = availableSources();
    const ids = sourceNames
      .map((name: string) => {
        const source = available.find((s: any) => 
          String(s.source_name).toLowerCase().trim() === String(name).toLowerCase().trim()
        );
        return source?.source_id ? Number(source.source_id) : null;
      })
      .filter((id: number | null): id is number => id !== null && Number.isFinite(id));
    // When no sources are selected but we have sources for the day, default to all so the map shows data (same as races: one "segment" selected)
    if (ids.length === 0 && available.length > 0) {
      const allIds = new Set(available.map((s: any) => Number(s.source_id)).filter((id: number) => Number.isFinite(id)));
      logDebug('[MapContainer] selectedSourceIds empty — defaulting to all sources for map', { count: allIds.size });
      return allIds;
    }
    const result = new Set(ids);
    logDebug('[MapContainer] selectedSourceIds computed (from filterStore)', {
      sourceNames,
      availableCount: available.length,
      mappedIds: Array.from(result),
      resultCount: result.size
    });
    if (result.size === 0 && available.length === 0) {
      logDebug('[MapContainer] selectedSourceIds is empty (no sources loaded yet)', { availableCount: available.length });
    }
    return result;
  });

  /**
   * Names of the boats currently drawn, for the alarm marker filter.
   *
   * Derived from `selectedSourceIds` rather than from the filter store directly, so it inherits the
   * "nothing selected means all" default above and cannot disagree with the tracks on screen. The
   * Alarms page lists the whole fleet's alarms; without this the layer placed a marker for every
   * one of them, including boats this map is not showing.
   */
  const alarmMarkerSourceNames = createMemo(() => {
    const available = availableSources();
    const ids = selectedSourceIds();
    const out: string[] = [];
    for (const id of ids) {
      const source = available.find((s: any) => Number(s.source_id) === Number(id));
      const name = source?.source_name;
      if (name) out.push(String(name));
    }
    // Null, not [], when nothing resolved: an empty-but-present list tells the layer to allow no
    // boat at all, so a selection this memo cannot name yet would hide every marker rather than
    // waiting. Null is "no opinion" - the same convention the track window uses.
    if (out.length === 0) {return null;}
    return out;
  });

  const setSelectedSourceIds = (ids: Set<number>) => {
    const available = availableSources();
    const sourceNames = Array.from(ids)
      .map((sourceId: number) => {
        const source = available.find((s: any) => Number(s.source_id) === sourceId);
        if (source?.source_name) return source.source_name;
        if (sourcesStore.isReady()) return sourcesStore.getSourceName(sourceId);
        return null;
      })
      .filter((name: string | null | undefined): name is string => !!name);
    filterStoreSetSelectedSources(sourceNames);
  };
  // Multi-only filters (race/leg) to avoid mutating global filterStore
  const [multiSelectedRaces, setMultiSelectedRaces] = createSignal<string[]>([]);
  const [multiSelectedLegs, setMultiSelectedLegs] = createSignal<string[]>([]);
  const [raceModeStatus, setRaceModeStatus] = createSignal<FleetRaceModeStatus>("pending");
  const effectiveSelectedRaces = createMemo(() => {
    const local = multiSelectedRaces();
    if (Array.isArray(local) && local.length > 0) return local;
    const store = selectedRacesTimeseries();
    return Array.isArray(store) ? store : [];
  });
  const normalizeRaceSelection = (races: (number | string)[]): string[] =>
    (Array.isArray(races) ? races : [])
      .filter((race) => race !== null && race !== undefined && String(race).trim() !== '')
      .map((race) => String(race));
  const applyClassifiedFleetRaces = (classified: ReturnType<typeof classifyFleetDateRacesResponse>) => {
    setIsTrainingHourMode(classified.trainingHourMode);
    setRaceModeStatus(classified.status);
    setRaceOptions(classified.raceOptions);
    const current = multiSelectedRaces();
    const nextSelection =
      current.length > 0 ? current : classified.initialSelection;
    if (nextSelection.length > 0 && current.length === 0) {
      setMultiSelectedRaces(nextSelection);
      setSelectedRacesTimeseries(nextSelection);
    }
    syncFleetTimeWindowForRaceSelection(nextSelection);
  };
  const syncFleetTimeWindowForRaceSelection = (selectedRaces: string[]) => {
    if (sourceMode !== "multi") return;
    const training = isTrainingHourMode();
    // Preserves the user's current window; only substitutes when Full is unavailable in this context.
    const nextWindow = fleetTimeWindowWhenRaceFilterApplied(training, selectedRaces, timeWindow());
    if (timeWindow() !== nextWindow) {
      setTimeWindow(nextWindow);
    }
  };
  const setNormalizedSelectedRaces = (races: (number | string)[]) => {
    const normalized = normalizeRaceSelection(races);
    if (sourceMode === 'multi') {
      setMultiSelectedRaces(normalized);
      syncFleetTimeWindowForRaceSelection(normalized);
    }
    setSelectedRacesTimeseries(normalized);
  };
  // Hovered boat source id (when animation stopped) for path highlight and boat label emphasis
  const [hoveredSourceId, setHoveredSourceId] = createSignal<number | null>(null);
  // Combined highlight: selected boats (from FleetMap click) + hovered when animation stopped
  const highlightedSourceIds = createMemo(() => {
    const selected = props.selectedBoatIds ?? new Set<number>();
    const hovered = hoveredSourceId();
    const playing = isPlaying();
    const addHover = hovered != null && !playing;
    return addHover ? new Set<number>([...selected, hovered]) : new Set<number>(selected);
  });

  /** Key for non-live multi mode track/boat block; when selection changes, keyed Show remounts so layers re-init with correct sources. */
  const multiModeLayersKey = createMemo(() => {
    if (sourceMode !== 'multi') return null;
    return Array.from(selectedSourceIds()).sort().join(',');
  });

  // When selected sources change in fleet map, clear all D3 boat overlays so the keyed block redraws only selected boats
  createEffect(() => {
    if (sourceMode !== 'multi') return;
    const ids = selectedSourceIds();
    // Defer clear so it runs after Apply has committed; clear from document to catch all boat overlays
    requestAnimationFrame(() => {
      try {
        const boats = document.querySelectorAll('[class*="boat-overlay-"]');
        boats.forEach((el) => el.remove());
        if (boats.length > 0) {
          logDebug('MapContainer: Cleared D3 boat overlays on source change', { removed: boats.length, selectedCount: ids.size });
        }
      } catch (e) {
        logDebug('MapContainer: Clear boats skipped', e);
      }
    });
  });

  // Update availableSources from sourcesStore when project changes
  // Multi mode: sync sources so selectedSourceIds and map data work
  createEffect(() => {
    if (sourceMode !== 'multi') return;
    
    // Wait for sourcesStore to be ready
    if (!sourcesStore.isReady()) return;
    
    // Track project changes to reset initialization flag
    const currentProjectId = untrack(() => persistantStore.selectedProjectId());
    if (currentProjectId !== lastProjectId) {
      hasInitializedFilterStoreSources = false;
      lastProjectId = currentProjectId;
    }
    
    const projectSources = sourcesStore.sources();
    if (projectSources.length === 0) return;
    
    // Map to format expected by MapContainer and sort by source_id (consistent with FleetPerformanceHistory)
    const sources = projectSources
      .map(s => ({
        source_id: s.source_id,
        source_name: s.source_name,
        dataset_id: null // Will be determined per-date when querying
      }))
      .sort((a, b) => {
        const aId = Number(a.source_id) || 0;
        const bId = Number(b.source_id) || 0;
        return aId - bId;
      });
    
    // Use untrack to read availableSources without creating a reactive dependency
    // This prevents the effect from re-running when availableSources changes
    const currentSources = untrack(() => availableSources());
    const currentIds = new Set(currentSources.map(s => s.source_id));
    const newIds = new Set(sources.map(s => s.source_id));
    
    if (currentIds.size !== newIds.size || 
        Array.from(newIds).some(id => !currentIds.has(id))) {
      logDebug('MapContainer: Updating availableSources from sourcesStore', { 
        count: sources.length,
        sources: sources.map(s => ({ id: s.source_id, name: s.source_name }))
      });
      setAvailableSources(sources);
      
      // Do not default filterStore to "all" when empty — let async onMount load from API (initializeSourceSelections)
      // so persisted fleetPerformanceSources is restored on reload. Only mark initialized when filterStore already has value.
      if (!hasInitializedFilterStoreSources) {
        const filterStoreSources = untrack(() => filterStoreSelectedSources());
        if (filterStoreSources.length > 0) {
          hasInitializedFilterStoreSources = true;
          logDebug('MapContainer: filterStore already has sources, marked initialized', { count: filterStoreSources.length });
        }
      }
    }
  });

  // Single mode (explore map): sync availableSources from sourcesStore so selectedSourceIds and track data work in splitscreen
  createEffect(() => {
    if (sourceMode !== 'single') return;
    if (!sourcesStore.isReady()) return;
    const projectSources = sourcesStore.sources();
    if (projectSources.length === 0) return;
    const sources = projectSources
      .map(s => ({ source_id: s.source_id, source_name: s.source_name, dataset_id: null as number | null }))
      .sort((a, b) => (Number(a.source_id) || 0) - (Number(b.source_id) || 0));
    const current = availableSources();
    const same = current.length === sources.length && sources.every((s, i) => current[i]?.source_id === s.source_id);
    if (!same) {
      logDebug('MapContainer: Updating availableSources (single mode) from sourcesStore', { count: sources.length });
      setAvailableSources(sources);
      const filterStoreSources = untrack(() => filterStoreSelectedSources());
      if (filterStoreSources.length === 0) {
        filterStoreSetSelectedSources(sources.map(s => s.source_name));
        logDebug('MapContainer: Initialized filterStore (single mode) with all sources', { count: sources.length });
      }
    }
  });

  // Once we've passed the guard and shown the map, never show Loading again (prevents flash and track loss on re-renders in split view). Must be after availableSources and sourceMode are defined.
  createEffect(() => {
    if (isSourcesReady() && (sourceMode === 'multi' || availableSources().length > 0)) {
      setHasShownMapOnce(true);
    }
  });

  // Note: We do NOT continuously sync from filterStore to selectedSourceIds
  // The initial sync happens when sources are loaded (in the availableSources effect above)
  // After that, selectedSourceIds is managed independently via MapSettings modal
  // This prevents infinite loops between the two stores

  const dataSourcesOptions = createMemo(() => {
    if (sourceMode !== 'multi') return [];
    
    const setRef = selectedSourceIds();
    logDebug('MapContainer: dataSourcesOptions build', { availableCount: availableSources().length, selected: Array.from(setRef) });
    return availableSources().map((s) => {
      const id = Number(s.source_id);
      return {
        key: `source-${id}`,
        label: s.source_name,
        type: 'toggle',
        signal: [
          () => selectedSourceIds().has(id),
          (value) => {
            // Support Set input (Select All/None) and boolean toggles
            if (value instanceof Set) {
              setSelectedSourceIds(new Set(Array.from(value).map(Number)));
              return;
            }
            const next = new Set(selectedSourceIds());
            const on = typeof value === 'function' ? value(next.has(id)) : value;
            if (on) next.add(id); else next.delete(id);
            setSelectedSourceIds(next);
          }
        ]
      };
    });
  });
  
  const [twaFilterOptions] = createSignal(["Upwind", "Downwind", "Reaching", "Port", "Stbd"]);

  // Overlay system - initialize on mount
  const [overlaysInitialized, setOverlaysInitialized] = createSignal(false);
  
  // Overlay enabled states - create persistent signals for each overlay
  // Use localStorage to persist overlay preferences
  // Get overlay state from persistent settings or localStorage fallback
  const getStoredOverlayState = (key, defaultValue) => {
    try {
      // First try to get from persistent settings (loaded from API)
      const mapOverlays = persistantStore.mapOverlays?.();
      if (mapOverlays && mapOverlays[key] !== undefined) {
        return mapOverlays[key];
      }
      // Fallback to localStorage for backward compatibility
      const stored = localStorage.getItem(`overlay_${key}`);
      if (stored === null) return defaultValue;
      return JSON.parse(stored);
    } catch {
      return defaultValue;
    }
  };

  // Save overlay state to persistent settings (which syncs to API)
  const setStoredOverlayState = (key, value) => {
    try {
      // Update persistent settings
      const currentOverlays = persistantStore.mapOverlays?.() || {};
      persistantStore.setMapOverlays?.({ ...currentOverlays, [key]: value });
      // Also update localStorage for immediate access
      localStorage.setItem(`overlay_${key}`, JSON.stringify(value));
    } catch (e) {
      logWarn(`Failed to store overlay state for ${key}:`, e);
    }
  };

  // Create signals with initial values from localStorage
  const [tracksEnabled, setTracksEnabledBase] = createSignal(getStoredOverlayState('tracks', true));
  // Track if we're updating from local toggle to prevent feedback loop
  let isTracksLocalUpdate = false;
  const setTracksEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(tracksEnabled()) : value;
    isTracksLocalUpdate = true;
    setTracksEnabledBase(newValue);
    setStoredOverlayState('tracks', newValue);
    // When tracks are disabled, also disable bad air
    if (!newValue && badAirEnabled()) {
      setBadAirEnabledBase(false);
      setStoredOverlayState('bad-air', false);
    }
    // Reset flag after a tick to allow effect to run for external changes
    setTimeout(() => { isTracksLocalUpdate = false; }, 0);
  };

  const [badAirEnabled, setBadAirEnabledBase] = createSignal(getStoredOverlayState('bad-air', false));
  // Track if we're updating from local toggle to prevent feedback loop
  let isBadAirLocalUpdate = false;
  const setBadAirEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(badAirEnabled()) : value;
    // Don't allow enabling bad air if tracks are disabled
    if (newValue && !tracksEnabled()) {
      return;
    }
    isBadAirLocalUpdate = true;
    setBadAirEnabledBase(newValue);
    setStoredOverlayState('bad-air', newValue);
    // Reset flag after a tick to allow effect to run for external changes
    setTimeout(() => { isBadAirLocalUpdate = false; }, 0);
  };

  const [markWindEnabled, setMarkWindEnabledBase] = createSignal(getStoredOverlayState('mark-wind', false));
  let isMarkWindLocalUpdate = false;
  const setMarkWindEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(markWindEnabled()) : value;
    isMarkWindLocalUpdate = true;
    setMarkWindEnabledBase(newValue);
    setStoredOverlayState('mark-wind', newValue);
    setTimeout(() => { isMarkWindLocalUpdate = false; }, 0);
  };

  // Boat wind: fold the boats' own TWS/TWD into the wind field and put a TWS arrow on each moving boat. Only
  // meaningful while the wind overlay is on. OPT-IN (default off): the wind field is the MARKS' field by
  // default — grade-checked, stationary readings — and the boats only join the interpolation once the user
  // asks to see their wind.
  const [boatWindEnabled, setBoatWindEnabledBase] = createSignal(getStoredOverlayState('boat-wind', false));
  let isBoatWindLocalUpdate = false;
  const setBoatWindEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(boatWindEnabled()) : value;
    if (newValue && !markWindEnabled()) return;
    isBoatWindLocalUpdate = true;
    setBoatWindEnabledBase(newValue);
    setStoredOverlayState('boat-wind', newValue);
    setTimeout(() => { isBoatWindLocalUpdate = false; }, 0);
  };

  createEffect(() => {
    const mapOverlays = persistantStore.mapOverlays?.();
    if (mapOverlays === undefined) return;
    if (isBoatWindLocalUpdate) return;
    if (mapOverlays['boat-wind'] !== undefined) {
      const storedValue = mapOverlays['boat-wind'];
      if (boatWindEnabled() !== storedValue) setBoatWindEnabledBase(storedValue);
    }
  });

  // Track if persistent settings have been loaded to force re-mount of RaceCourseLayer
  // Declared early so it can be used in createEffect below
  const [persistentSettingsLoaded, setPersistentSettingsLoaded] = createSignal(false);

  // Update tracksEnabled when persistent settings load (but not from local updates)
  createEffect(() => {
    const mapOverlays = persistantStore.mapOverlays?.();
    if (mapOverlays === undefined) return;
    
    // Skip if this is a local update to prevent feedback loop
    if (isTracksLocalUpdate) return;
    
    // Update tracksEnabled if it's defined in persistent settings
    if (mapOverlays['tracks'] !== undefined) {
      const currentValue = tracksEnabled();
      const storedValue = mapOverlays['tracks'];
      if (currentValue !== storedValue) {
        setTracksEnabledBase(storedValue);
        // When tracks are disabled, also disable bad air
        if (!storedValue && badAirEnabled()) {
          setBadAirEnabledBase(false);
          setStoredOverlayState('bad-air', false);
        }
      }
    }
    
    // Mark that persistent settings have been loaded (if not already marked)
    if (!persistentSettingsLoaded()) {
      setPersistentSettingsLoaded(true);
    }
  });

  // Ensure bad air is disabled when tracks are disabled
  createEffect(() => {
    if (!tracksEnabled() && badAirEnabled()) {
      setBadAirEnabledBase(false);
      setStoredOverlayState('bad-air', false);
    }
  });

  // Update badAirEnabled when persistent settings load (but not from local updates)
  createEffect(() => {
    const mapOverlays = persistantStore.mapOverlays?.();
    if (mapOverlays === undefined) return;
    
    // Skip if this is a local update to prevent feedback loop
    if (isBadAirLocalUpdate) return;
    
    // Update badAirEnabled if it's defined in persistent settings
    // But only if tracks are enabled (bad air requires tracks)
    if (mapOverlays['bad-air'] !== undefined && tracksEnabled()) {
      const currentValue = badAirEnabled();
      const storedValue = mapOverlays['bad-air'];
      if (currentValue !== storedValue) {
        setBadAirEnabledBase(storedValue);
      }
    }
    
    // Mark that persistent settings have been loaded (if not already marked)
    if (!persistentSettingsLoaded()) {
      setPersistentSettingsLoaded(true);
    }
  });

  // Update markWindEnabled when persistent settings load (but not from local updates)
  createEffect(() => {
    const mapOverlays = persistantStore.mapOverlays?.();
    if (mapOverlays === undefined) return;
    if (isMarkWindLocalUpdate) return;
    if (mapOverlays['mark-wind'] !== undefined) {
      const currentValue = markWindEnabled();
      const storedValue = mapOverlays['mark-wind'];
      if (currentValue !== storedValue) {
        setMarkWindEnabledBase(storedValue);
      }
    }
  });

  const [boundariesEnabled, setBoundariesEnabledBase] = createSignal(getStoredOverlayState('boundaries', true));
  // Track if we're updating from local toggle to prevent feedback loop
  let isLocalUpdate = false;
  const setBoundariesEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(boundariesEnabled()) : value;
    isLocalUpdate = true;
    setBoundariesEnabledBase(newValue);
    setStoredOverlayState('boundaries', newValue);
    // Reset flag after a tick to allow effect to run for external changes
    setTimeout(() => { isLocalUpdate = false; }, 0);
  };
  
  // Update boundariesEnabled when persistent settings load (but not from local updates)
  createEffect(() => {
    const mapOverlays = persistantStore.mapOverlays?.();
    if (mapOverlays === undefined) return;
    
    // Skip if this is a local update to prevent feedback loop
    if (isLocalUpdate) return;
    
    // Update boundariesEnabled if it's defined in persistent settings
    if (mapOverlays['boundaries'] !== undefined) {
      const currentValue = boundariesEnabled();
      const storedValue = mapOverlays['boundaries'];
      if (currentValue !== storedValue) {
        logDebug('MapContainer: Updating boundariesEnabled from persistent settings', { currentValue, storedValue });
        setBoundariesEnabledBase(storedValue);
      }
    }
    
    // Mark that persistent settings have been loaded (mapOverlays exists means store is initialized)
    if (!persistentSettingsLoaded()) {
      setPersistentSettingsLoaded(true);
    }
  });

  const [windArrowsEnabled, setWindArrowsEnabledBase] = createSignal(getStoredOverlayState('wind-arrows', false));
  const setWindArrowsEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(windArrowsEnabled()) : value;
    setWindArrowsEnabledBase(newValue);
    setStoredOverlayState('wind-arrows', newValue);
  };

  const [currentArrowsEnabled, setCurrentArrowsEnabledBase] = createSignal(getStoredOverlayState('current-arrows', false));
  const setCurrentArrowsEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(currentArrowsEnabled()) : value;
    setCurrentArrowsEnabledBase(newValue);
    setStoredOverlayState('current-arrows', newValue);
  };

  const [windContoursEnabled, setWindContoursEnabledBase] = createSignal(getStoredOverlayState('wind-contours', false));
  const setWindContoursEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(windContoursEnabled()) : value;
    setWindContoursEnabledBase(newValue);
    setStoredOverlayState('wind-contours', newValue);
  };

  const [maneuversEnabled, setManeuversEnabledBase] = createSignal(getStoredOverlayState('maneuvers', true));
  const setManeuversEnabled = (value) => {
    const newValue = typeof value === 'function' ? value(maneuversEnabled()) : value;
    setManeuversEnabledBase(newValue);
    setStoredOverlayState('maneuvers', newValue);
  };

  // Memo for combined maneuvers enabled state (maneuversEnabled && zoom threshold)
  // This prevents creating computations outside reactive context when accessed in setTimeout callbacks
  const maneuversEnabledComputed = createMemo(() => maneuversEnabled() && showManeuvers());

  // Data overlay name (from Data Overlay combo in MapSettings). Empty = NONE; otherwise overlay object_name from user_objects (parent_name 'overlay').
  const [dataOverlayName, setDataOverlayNameBase] = createSignal(getStoredOverlayState('dataOverlayName', ''));
  const setDataOverlayName = (value: string) => {
    const name = typeof value === 'string' ? value.trim() : '';
    setDataOverlayNameBase(name);
    setStoredOverlayState('dataOverlayName', name);
    setStoredOverlayState('dataOverlayChoiceApplied', true);
  };

  // Reference boat (fleet map only): boat NAME string (empty = NONE). Drives the timeline's
  // "VMG delta (m)" mode. Persisted in overlay state and included in shared dashboard URLs.
  const [referenceBoatName, setReferenceBoatNameBase] = createSignal<string>(getStoredOverlayState('referenceBoatName', ''));
  let isReferenceBoatLocalUpdate = false;
  const setReferenceBoatName = (value: string) => {
    const name = typeof value === 'string' ? value.trim() : '';
    isReferenceBoatLocalUpdate = true;
    setReferenceBoatNameBase(name);
    setStoredOverlayState('referenceBoatName', name);
    setTimeout(() => { isReferenceBoatLocalUpdate = false; }, 0);
  };

  // Sync reference boat from persistent/shared settings when they load (skip our own local writes).
  createEffect(() => {
    const mapOverlays = persistantStore.mapOverlays?.();
    if (mapOverlays === undefined) return;
    if (isReferenceBoatLocalUpdate) return;
    if (mapOverlays['referenceBoatName'] !== undefined) {
      const stored = String(mapOverlays['referenceBoatName'] ?? '');
      if (stored !== referenceBoatName()) setReferenceBoatNameBase(stored);
    }
  });

  /** Boat names available as reference-boat options (fleet map). */
  const referenceBoatOptions = createMemo<string[]>(() =>
    availableSources().map((s) => String(s.source_name || '')).filter(Boolean)
  );

  /** Resolve reference boat NAME -> numeric source_id (case-insensitive). null when unset/unresolved. */
  const referenceSourceId = createMemo<number | null>(() => {
    if (sourceMode !== 'multi') return null;
    const name = String(referenceBoatName() || '').trim().toLowerCase();
    if (!name) return null;
    const match = availableSources().find(
      (s) => String(s.source_name || '').trim().toLowerCase() === name
    );
    if (match && Number.isFinite(Number(match.source_id))) return Number(match.source_id);
    // Fallback to sourcesStore if availableSources isn't populated yet.
    if (sourcesStore.isReady()) {
      const s = sourcesStore.sources().find(
        (x) => String(x.source_name || '').trim().toLowerCase() === name
      );
      if (s && Number.isFinite(Number(s.source_id))) return Number(s.source_id);
    }
    return null;
  });

  // Map dimensions for overlays
  const [mapDimensions, setMapDimensions] = createStore({ width: 1400, height: 900 });
  
  // Track if we're in split view for proper height calculation
  const [isInSplitView, setIsInSplitView] = createSignal(false);
  // Track hover over map area for show-on-hover play/pause controls
  const [isHoveringMap, setIsHoveringMap] = createSignal(false);
  const [isHoveringTimeline, setIsHoveringTimeline] = createSignal(false);
  
  // Check for split view on mount and when component updates.
  // Only update signal when value changes to avoid redundant effect re-runs (reduces stall when split view is open).
  createEffect(() => {
    const checkSplitView = () => {
      const hasSplitViewContent = document.querySelector('.split-view-content') !== null;
      const hasSplitPanel = document.querySelector('.split-panel') !== null;
      const splitViewExists = hasSplitViewContent || hasSplitPanel;
      untrack(() => {
        if (splitViewExists !== isInSplitView()) setIsInSplitView(splitViewExists);
      });
    };

    checkSplitView();
    const rafId = requestAnimationFrame(() => checkSplitView());
    const interval = setInterval(checkSplitView, 1000);
    // onCleanup, not a returned function: Solid ignores a createEffect return value, so this 1s
    // DOM probe outlived the component and a fresh one started every time a map was opened.
    onCleanup(() => {
      cancelAnimationFrame(rafId);
      clearInterval(interval);
    });
  });

  // Trigger map resize when split view is detected
  createEffect(() => {
    if (isInSplitView()) {
      const currentMap = map();
      if (currentMap) {
        logDebug('MapContainer: Split view detected, triggering map resize');
        setTimeout(() => {
          triggerMapResize();
        }, 100);
      }
    }
  });
  
  const [values, setValues] = createSignal([]);
  
  // Preserve the last known full-range dataset so we can restore on clear
  const [baseValues, setBaseValues] = createSignal([]);
  
  // Flag to track if filter options have been extracted
  let hasExtractedFilters = false;
  let hasPerformedInitialFit = false; // Track if we've done the initial fit bounds
  let hasAppliedInitialBearing = false;
  const [initialFitComplete, setInitialFitComplete] = createSignal(false);
  let lastHandledFilterSignature: string | undefined = undefined; // Track race/leg filter for zoom-on-filter-change (declared early for createEffect below)
  
  // Non-live data: fixed at 1Hz
  const samplingFrequency = createMemo(() => 1);
  
  // Store map state for persistence
  const [mapState, setMapState] = createSignal({
    center: [0, 0],
    zoom: 14,
    bearing: 0
  });
  
  // Flag to track if this is the initial map draw
  let isInitialMapDraw = true;
  
  // Track the last data length to detect significant data changes
  let lastDataLength = 0;

  // Add a signal to track map interaction state
  const [isMapMoving, setIsMapMoving] = createSignal(false);
  const [mapMovementTimeout, setMapMovementTimeout] = createSignal(null);
  const [isProgrammaticMovement, setIsProgrammaticMovement] = createSignal(false);

  // Add signal to track playback state before map movement
  const [wasPlayingBeforeMapMove, setWasPlayingBeforeMapMove] = createSignal(false);

  // Add a signal to track the "true" selected time that won't be affected by map operations
  const [stableSelectedTime, setStableSelectedTime] = createSignal(null);

  // Add comprehensive resize detection for responsive map sizing
  let resizeTimeout;
  let resizeObserver = null;
  let mutationObserver = null;
  let tilesAvailabilityTimeout = null; // For cleanup in onCleanup
  
  const triggerMapResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const currentMap = map();
      const container = mapContainer();
      
      if (currentMap && container) {
        logDebug('🔍 MapContainer: Triggering map resize');
        logDebug('🔍 MapContainer: Container dimensions:', {
          width: container.offsetWidth,
          height: container.offsetHeight,
          clientWidth: container.clientWidth,
          clientHeight: container.clientHeight
        });
        
        // Force map to recalculate its size
        currentMap.resize();

        const canvasContainer = container.querySelector('.mapboxgl-canvas-container') as HTMLElement | null;
        if (canvasContainer) {
          setMapDimensions({
            width: canvasContainer.offsetWidth || container.offsetWidth || 1400,
            height: canvasContainer.offsetHeight || container.offsetHeight || 900
          });
        }
        
        // Log map dimensions after resize
        logDebug('🔍 MapContainer: Map dimensions after resize:', {
          width: currentMap.getContainer().offsetWidth,
          height: currentMap.getContainer().offsetHeight
        });
      }
    }, 100); // Shorter debounce for better responsiveness
  };

  const handleResize = () => {
    triggerMapResize();
  };

  const setupResizeObservers = () => {
    const container = mapContainer();
    if (!container) return;

    // ResizeObserver to watch for container size changes
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        logDebug('🔍 MapContainer: ResizeObserver triggered');
        triggerMapResize();
      });
      resizeObserver.observe(container);
      
      // Also observe split-panel if we're in split view
      const splitPanel = container.closest('.split-panel');
      if (splitPanel) {
        logDebug('🔍 MapContainer: Observing split-panel for resize');
        resizeObserver.observe(splitPanel);
      }
    }

    // MutationObserver to watch for sidebar class changes
    const sidebar = document.querySelector('.sidebar');
    if (sidebar && window.MutationObserver) {
      mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            logDebug('🔍 MapContainer: Sidebar class changed, triggering map resize');
            // Add a small delay to allow CSS transition to complete
            setTimeout(triggerMapResize, 350);
          }
        });
      });
      mutationObserver.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Fallback: Periodic check for container size changes (every 2 seconds)
    // This ensures we catch any resize events that the observers might miss
    const periodicCheck = setInterval(() => {
      const currentMap = map();
      const currentContainer = mapContainer();
      
      if (currentMap && currentContainer) {
        const mapContainer = currentMap.getContainer();
        const containerWidth = currentContainer.offsetWidth;
        const containerHeight = currentContainer.offsetHeight;
        const mapWidth = mapContainer.offsetWidth;
        const mapHeight = mapContainer.offsetHeight;
        
        // If container size doesn't match map size, trigger resize
        if (Math.abs(containerWidth - mapWidth) > 5 || Math.abs(containerHeight - mapHeight) > 5) {
          logDebug('🔍 MapContainer: Periodic check detected size mismatch, triggering resize');
          triggerMapResize();
        }
      }
    }, 2000);

    // Store interval ID for cleanup
    window.mapPeriodicCheck = periodicCheck;
  };

  // Helper function to create event ID signature for data change detection
  const getEventIdSignature = (data) => {
    if (!data || data.length === 0) return 'empty';
    
    // Count event_id occurrences
    const eventIdCounts = {};
    data.forEach(point => {
      const eventId = point.event_id || 0;
      eventIdCounts[eventId] = (eventIdCounts[eventId] || 0) + 1;
    });
    
    // Create sorted signature of unique event IDs
    const uniqueEventIds = Object.keys(eventIdCounts)
      .filter(id => parseInt(id) > 0)
      .sort((a, b) => parseInt(a) - parseInt(b));
    
    return uniqueEventIds.length > 0 ? uniqueEventIds.join(',') : '0';
  };

  // Smart memo for track data with intelligent update logic
  let lastTrackData = null;
  let lastTrackDataTime = 0;
  let lastDataHash = null;
  let forceTrackDataUpdate = false; // Flag to force track data update
  
  const trackData = createMemo(() => {
    const currentTimeWindow = timeWindow();
    const currentValues = values();
    const now = Date.now();

    // Always return a new array reference to ensure reactivity
    // This ensures overlays and other consumers get fresh data even if content is similar
    const nextValues = Array.isArray(currentValues) ? [...currentValues] : currentValues;
    
    // If timeWindow is 0 (full timeline), use intelligent update logic
    if (currentTimeWindow === 0) {
      // Create a hash that includes event_id assignments to detect selection changes
      const dataHash = currentValues.length > 0 ? 
        `${currentValues.length}_${currentValues[0]?.Datetime}_${currentValues[currentValues.length - 1]?.Datetime}_${getEventIdSignature(currentValues)}` : 
        'empty';
      
      // Check if data has really changed
      const dataChanged = dataHash !== lastDataHash;
      
      // Check if enough time has passed (2 seconds)
      const timePassed = now - lastTrackDataTime > 2000;
      
      // Removed frequent caching decision log
      
      // In multi-mode, MultiMapTimeSeries already applies race filtering before calling onMapUpdate
      // so we don't need to filter again here - just use the data as-is

      // Update if data really changed OR if 2+ seconds have passed OR if forced
      if (dataChanged || timePassed || forceTrackDataUpdate || !lastTrackData) {
        lastTrackData = nextValues;
        lastTrackDataTime = now;
        lastDataHash = dataHash;
        forceTrackDataUpdate = false; // Reset force flag
        // Only log when data actually changes (not on every memo call)
        if (dataChanged) {
          logDebug('MapContainer: Track data updated', { length: nextValues.length });
        }
        return nextValues;
      }
      
      // Return fresh array reference even if content is the same (ensures reactivity)
      return Array.isArray(lastTrackData) ? [...lastTrackData] : (lastTrackData || nextValues);
    }
    
    // For time windows > 0, follow values changes normally
    // In multi-mode, MultiMapTimeSeries already applies race filtering
    lastTrackData = nextValues;
    lastTrackDataTime = now;
    lastDataHash = nextValues.length > 0 ? 
      `${nextValues.length}_${nextValues[0]?.Datetime}_${nextValues[nextValues.length - 1]?.Datetime}` : 
      'empty';
    return nextValues;
  });

  // When playing with time window, use throttled smooth time for track/wind/bad air (~10fps) to avoid 60fps recompute.
  // Boat uses smoothPlaybackTime() directly at 60fps; track/wind/bad air use this.
  const effectivePlaybackTime = createMemo(() => {
    const currentTimeWindow = timeWindow();
    const currentlyPlaying = isPlaying();
    if (currentTimeWindow === 0 || !currentlyPlaying) return null;
    return smoothPlaybackTimeForTrack();
  });

  /**
   * Averaged wind from the boats that are actually sailing at the current playhead — fed to the wind-field
   * overlay and drawn as a TWS arrow on each qualifying boat, exactly as on the live map.
   *
   * Paced to whole seconds: scrubbing changes selectedTime at frame rate and each sample pass walks the loaded
   * track rows, while a boat's 20s-averaged wind cannot meaningfully change faster than that. Grouping is its own
   * memo so it is only redone when the loaded data changes, not on every tick.
   */
  const boatWindRowsBySource = createMemo(() => {
    if (!markWindEnabled() || !boatWindEnabled()) return null;
    // Single-source Explore rows carry no source_id; attribute them to the selected source so that map gets a
    // boat wind sample too.
    return groupRowsBySourceId(trackData() || [], Number(persistantStore.selectedSourceId?.() ?? NaN));
  });

  const boatWindTimeBucket = createMemo(() => {
    const t = effectivePlaybackTime() ?? selectedTime();
    const ms = t instanceof Date ? t.getTime() : 0;
    return Math.floor(ms / 1000);
  });

  const boatWindSamples = createMemo(() => {
    const rowsBySource = boatWindRowsBySource();
    if (!rowsBySource || rowsBySource.size === 0) return [];
    const endMs = boatWindTimeBucket() * 1000;
    // Wind prefers the SGP fused solution, falling back to this class's canonical channel when the loaded
    // columns don't carry it (resolved per source inside collectBoatWindSamples).
    const fields = {
      lat: latName(),
      lng: lngName(),
      tws: ['Tws_sgp_kph', twsName()],
      twd: ['Twd_sgp_deg', twdName()],
      bsp: bspName(),
    };
    return collectBoatWindSamples(rowsBySource, fields, { endMs: endMs > 0 ? endMs : undefined });
  });

  /**
   * The same rows as a TRAIL of time-tagged readings (one per boat per 10s bin over the last few minutes) for the
   * wind field to advect downwind. Paced off the same 1s bucket as boatWindSamples, and a single pass over the
   * rows regardless of how many bins come out of it.
   */
  const boatWindHistory = createMemo(() => {
    const rowsBySource = boatWindRowsBySource();
    if (!rowsBySource || rowsBySource.size === 0) return [];
    const endMs = boatWindTimeBucket() * 1000;
    const fields = {
      lat: latName(),
      lng: lngName(),
      tws: ['Tws_sgp_kph', twsName()],
      twd: ['Twd_sgp_deg', twdName()],
      bsp: bspName(),
    };
    return collectBoatHistorySamples(rowsBySource, fields, { endMs: endMs > 0 ? endMs : undefined });
  });

  /** Boat wind keyed by source id, for the boat layers on this map (which group their data by source id). */
  const boatWindBySourceId = createMemo(() => {
    const out = new Map<number, { tws: number; twd: number }>();
    for (const s of boatWindSamples()) out.set(s.sourceId, { tws: s.tws, twd: s.twd });
    return out;
  });

  // Get time range from cut or brush selection for zoom-to-selection (declared before createEffect that uses getDataForBounds/calculateBounds)
  const getSelectionTimeRange = (): { start: Date; end: Date } | null => {
    const cuts = cutEvents();
    if (Array.isArray(cuts) && cuts.length > 0) {
      const first = cuts[0];
      if (first && typeof first === 'object' && 'start_time' in first && 'end_time' in first) {
        return { start: new Date(first.start_time), end: new Date(first.end_time) };
      }
    }
    const range = selectedRange();
    if (Array.isArray(range) && range.length > 0) {
      const first = range[0];
      if (first && typeof first === 'object' && 'start_time' in first && 'end_time' in first) {
        return { start: new Date(first.start_time), end: new Date(first.end_time) };
      }
    }
    return null;
  };

  const getDataForBounds = (data: any[]): any[] => {
    if (!data || data.length === 0) return data;
    const range = getSelectionTimeRange();
    if (!range) return data;
    const startMs = range.start.getTime();
    const endMs = range.end.getTime();
    return data.filter((point: any) => {
      const dt = point.Datetime;
      if (!dt) return false;
      const t = dt instanceof Date ? dt.getTime() : new Date(dt).getTime();
      return t >= startMs && t <= endMs;
    });
  };

  const calculateBounds = (data: any): [[number, number], [number, number]] | null => {
    if (!data || data.length === 0) {
      logDebug('MapContainer: calculateBounds - no data');
      return null;
    }
    const latField = latName();
    const lngField = lngName();
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    let validPoints = 0;
    data.forEach((point: any) => {
      const lat = point[latField] ?? point[latField?.toLowerCase()] ?? point[latField?.toUpperCase()] ?? point.Lat ?? point.lat ?? point.LAT;
      const lng = point[lngField] ?? point[lngField?.toLowerCase()] ?? point[lngField?.toUpperCase()] ?? point.Lng ?? point.lng ?? point.LNG;
      if (lat != null && lng != null) {
        const latNum = Number(lat);
        const lngNum = Number(lng);
        if (!isNaN(latNum) && !isNaN(lngNum)) {
          minLng = Math.min(minLng, lngNum);
          maxLng = Math.max(maxLng, lngNum);
          minLat = Math.min(minLat, latNum);
          maxLat = Math.max(maxLat, latNum);
          validPoints++;
        }
      }
    });
    logDebug('MapContainer: calculateBounds', { totalPoints: data.length, validPoints, latField, lngField });
    if (minLng === Infinity || maxLng === -Infinity || minLat === Infinity || maxLat === -Infinity || validPoints === 0) {
      logDebug('MapContainer: calculateBounds - no valid coordinates found');
      return null;
    }
    return [[minLng, minLat], [maxLng, maxLat]];
  };

  // Reset initial fit flag and filter signature when class/project/dataset changes
  createEffect(() => {
    const className = persistantStore.selectedClassName?.();
    const projectId = persistantStore.selectedProjectId?.();
    const datasetId = persistantStore.selectedDatasetId?.();
    
    // Reset flag when selection changes so we can fit bounds for new data
    hasPerformedInitialFit = false;
    hasAppliedInitialBearing = false;
    setInitialFitComplete(false);
    lastHandledFilterSignature = undefined;
    logDebug('MapContainer: Reset initial fit flag', { className, projectId, datasetId });
  });

  // Auto-fit bounds when map data is first loaded (zoom to selection when cut/brush is active)
  createEffect(() => {
    const currentMap = map();
    const data = trackData();
    const dataForBounds = getDataForBounds(data || []);
    
    // Only fit bounds on first data load when map is ready
    if (currentMap && data && data.length > 0 && !hasPerformedInitialFit) {
      const boundsData = dataForBounds.length > 0 ? dataForBounds : data;
      // Wait for map to be fully loaded
      if (currentMap.loaded()) {
        const bounds = calculateBounds(boundsData);
        if (bounds) {
          logDebug('MapContainer: Auto-fitting bounds on first data load', {
            dataLength: data.length,
            selectionDataLength: dataForBounds.length,
            bounds
          });
          hasPerformedInitialFit = true;
          setInitialFitComplete(true);
          setIsProgrammaticMovement(true);
          fitBoundsPreservingOrientation(currentMap, bounds, { duration: 1000 });
          setTimeout(() => setIsProgrammaticMovement(false), 1100);
        } else {
          logDebug('MapContainer: Auto-fit failed - calculateBounds returned null');
        }
      } else {
        logDebug('MapContainer: Map not loaded yet, waiting for load event');
        // Wait for map to load
        currentMap.once('load', () => {
          const bounds = calculateBounds(boundsData);
          if (bounds && !hasPerformedInitialFit) {
            logDebug('MapContainer: Auto-fitting bounds after map load', {
              dataLength: data.length,
              selectionDataLength: dataForBounds.length,
              bounds
            });
            hasPerformedInitialFit = true;
            setInitialFitComplete(true);
            setIsProgrammaticMovement(true);
            fitBoundsPreservingOrientation(currentMap, bounds, { duration: 1000 });
            setTimeout(() => setIsProgrammaticMovement(false), 1100);
          } else if (!bounds) {
            logDebug('MapContainer: Auto-fit after load failed - calculateBounds returned null');
          }
        });
      }
    }
  });

  // Calculate average TWD and TWS for wind arrow
  // Live: read streamingStore (tracks/boats already do) — avoid pumping full history through values() on every WS tick.
  // Non-live: use trackData() like other map consumers.
  const windData = createMemo(() => {
    const data = trackData() || [];
    const currentTime = selectedTime();
    const effectiveTime = effectivePlaybackTime();
    // Use effective time (boat position) when available so wind is at boat, not ahead
    const timeRef = effectiveTime ?? currentTime;

    if (!data || data.length === 0) {
      logDebug('WindArrow: No wind source data available');
      return { tws: undefined, twd: undefined };
    }

    // Filter data to points at or before timeRef (never include future points)
    let filteredData = data;
    if (timeRef) {
      // Use a 10 second window ending at timeRef for averaging (past only, no future)
      const windowEnd = timeRef;
      const windowStart = new Date(timeRef.getTime() - 10000); // 10 seconds before

      filteredData = data.filter((point: any) => {
        const pointTime = point.Datetime;
        if (!pointTime) return false;

        const pointDate = pointTime instanceof Date ? pointTime : new Date(pointTime);
        return pointDate >= windowStart && pointDate <= windowEnd;
      });

      // If no data in window, use all data up to timeRef
      if (filteredData.length === 0) {
        filteredData = data.filter((point: any) => {
          const pointTime = point.Datetime;
          if (!pointTime) return false;
          const pointDate = pointTime instanceof Date ? pointTime : new Date(pointTime);
          return pointDate <= timeRef;
        });
      }
    }

    if (filteredData.length === 0) {
      logDebug('WindArrow: No data after filtering', { 
        totalDataLength: data.length, 
        timeRef: timeRef?.toISOString(),
        hasTimeRef: !!timeRef
      });
      return { tws: undefined, twd: undefined };
    }

    // Collect valid TWD and TWS values
    const twdValues: number[] = [];
    const twsValues: number[] = [];
    const twdField = twdName();
    const twsField = twsName();

    filteredData.forEach((point: any) => {
      // Check both lowercase and capitalized field names, using dynamic channel names with case-insensitive fallbacks
      const twd = point[twdField] ?? point[twdField.toLowerCase()] ?? point[twdField.toUpperCase()] ?? point.Twd ?? point.twd ?? point.TWD;
      const tws = point[twsField] ?? point[twsField.toLowerCase()] ?? point[twsField.toUpperCase()] ?? point.Tws ?? point.tws ?? point.TWS;

      if (twd !== undefined && twd !== null && !isNaN(Number(twd))) {
        twdValues.push(Number(twd));
      }
      if (tws !== undefined && tws !== null && !isNaN(Number(tws))) {
        twsValues.push(Number(tws));
      }
    });

    if (twdValues.length === 0 || twsValues.length === 0) {
      // Sample first point to see what fields are available
      const samplePoint = filteredData[0];
      logDebug('WindArrow: No valid TWD/TWS values found', { 
        twdField, 
        twsField,
        filteredDataLength: filteredData.length,
        twdValuesFound: twdValues.length,
        twsValuesFound: twsValues.length,
        samplePointKeys: samplePoint ? Object.keys(samplePoint).filter(k => k.toLowerCase().includes('tw')) : null,
        sampleTwd: samplePoint ? (samplePoint[twdField] ?? samplePoint[twdField.toLowerCase()] ?? samplePoint[twdField.toUpperCase()] ?? samplePoint.Twd ?? samplePoint.twd ?? samplePoint.TWD) : null,
        sampleTws: samplePoint ? (samplePoint[twsField] ?? samplePoint[twsField.toLowerCase()] ?? samplePoint[twsField.toUpperCase()] ?? samplePoint.Tws ?? samplePoint.tws ?? samplePoint.TWS) : null
      });
      return { tws: undefined, twd: undefined };
    }

    // Calculate mean TWD (circular mean for angles)
    const meanTWD = (() => {
      const sinSum = twdValues.reduce((sum, angle) => sum + Math.sin(angle * (Math.PI / 180)), 0);
      const cosSum = twdValues.reduce((sum, angle) => sum + Math.cos(angle * (Math.PI / 180)), 0);
      const meanAngle = Math.atan2(sinSum, cosSum) * (180 / Math.PI);
      return ((meanAngle % 360) + 360) % 360; // Normalize to 0-360
    })();

    // Calculate mean TWS (linear mean)
    const meanTWS = twsValues.reduce((sum, val) => sum + val, 0) / twsValues.length;

    return { tws: meanTWS, twd: meanTWD };
  });

  // Bearing from saved wind-arrow index (0–4) + current TWD. Index is the single source of truth; we never use saved bearing for orientation.
  const WIND_ROTATION_STORAGE_KEY = 'map_wind_rotation_preference';

  const getSavedWindRotationIndex = (): number => {
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem(WIND_ROTATION_STORAGE_KEY);
        if (saved !== null) {
          const v = parseInt(saved, 10);
          if (v >= 0 && v <= 4) return v;
        }
      }
    } catch {
      // ignore
    }
    return 0;
  };

  const getBearingFromWindRotationIndex = (twd: number | undefined): number => {
    if (twd === undefined) return 0;
    const index = getSavedWindRotationIndex();
    let bearing = 0;
    if (index === 1) bearing = twd;
    else if (index === 2) bearing = twd + 90;
    else if (index === 3) bearing = twd + 180;
    else if (index === 4) bearing = twd + 270;
    while (bearing > 180) bearing -= 360;
    while (bearing < -180) bearing += 360;
    return bearing;
  };

  /** North-up (index 0) needs no TWD; wind-aligned modes need live TWD before rotating. */
  const canApplySavedBearing = (twd: number | undefined): boolean => {
    return getSavedWindRotationIndex() === 0 || twd !== undefined;
  };

  /** Apply saved wind-arrow rotation after programmatic fitBounds. */
  const restoreSavedBearing = () => {
    if (hasAppliedInitialBearing) return;
    const currentMap = map();
    if (!currentMap) return;

    const twd = windData()?.twd;
    if (!canApplySavedBearing(twd)) {
      logDebug('MapContainer: deferring bearing restore until TWD is available', {
        rotationIndex: getSavedWindRotationIndex(),
      });
      return;
    }

    const apply = () => {
      const m = map();
      if (!m || hasAppliedInitialBearing) return;
      const currentTwd = windData()?.twd;
      if (!canApplySavedBearing(currentTwd)) return;
      const bearing = getBearingFromWindRotationIndex(currentTwd);
      m.setBearing(bearing);
      hasAppliedInitialBearing = true;
      logDebug('MapContainer: restored bearing from wind rotation preference', {
        rotationIndex: getSavedWindRotationIndex(),
        twd: currentTwd,
        bearing,
      });
    };
    currentMap.once('moveend', apply);
    setTimeout(apply, 150);
  };

  createEffect(() => {
    const currentMap = map();
    const wind = windData();
    if (hasAppliedInitialBearing || !currentMap || !initialFitComplete()) return;
    if (!canApplySavedBearing(wind?.twd)) return;
    restoreSavedBearing();
  });

  /** Retry bearing restore while TWD may still be loading on a fresh dataset. */
  createEffect(() => {
    if (!initialFitComplete() || hasAppliedInitialBearing) return;
    const retryMs = [800, 2000, 4000];
    const timers = retryMs.map((ms) => setTimeout(() => restoreSavedBearing(), ms));
    onCleanup(() => timers.forEach((id) => clearTimeout(id)));
  });

  // Helper function to create new abort controller - only reset if not initializing
  const resetAbortController = () => {
    if (!isInitializing) {
      abortController.abort();
      abortController = new AbortController();
    }
  };

  // Data fetching removed - MapTimeSeries is the data controller

  // Initialize map
  const initMap = async () => {
    try {
      logDebug('=== CACHE BUST v6 - initMap called ===');
      
      try {
        logDebug('Attempting to import mapbox-gl...');
        const mapboxModule = await import("mapbox-gl");
        mapboxgl = mapboxModule.default || mapboxModule;
        logDebug('SUCCESS: mapboxgl imported, type:', typeof mapboxgl);
      } catch (importError) {
        logError('FAILED to import mapbox-gl:', importError);
        return;
      }
      
      if (!mapboxgl) {
        logError('mapboxgl is null/undefined after import');
        return;
      }
      
      logDebug('Setting mapbox access token...');
      if (!config.MAPBOX_TOKEN || config.MAPBOX_TOKEN.trim() === '') {
        logError('Mapbox access token is not configured. Please set VITE_MAPBOX_TOKEN in your environment variables.');
        return;
      }
      mapboxgl.accessToken = config.MAPBOX_TOKEN;
      logDebug('Mapbox token set:', config.MAPBOX_TOKEN.substring(0, 10) + '...');
      logDebug('Mapbox style:', config.MAPBOX_STYLE);
      logDebug('Access token set');

      // Wait for mapContainer to be available
      let attempts = 0;
      while (!mapContainer() && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!mapContainer()) {
        logError("mapContainer is not set after waiting");
        return;
      }
      
      logDebug('mapContainer is available');

      // Try to restore saved map state from localStorage
      const savedStateKey = `mapState_${objectName}`;
      let savedState = null;
      try {
        const saved = localStorage.getItem(savedStateKey);
        if (saved) {
          savedState = JSON.parse(saved);
        }
      } catch (e) {
        logWarn('Could not parse saved map state:', e);
      }
      logDebug('Saved state:', savedState);

      logDebug('Creating mapbox map...');
      logDebug('Map container element:', mapContainer());
      logDebug('Map container dimensions:', {
        width: mapContainer()?.offsetWidth,
        height: mapContainer()?.offsetHeight,
        clientWidth: mapContainer()?.clientWidth,
        clientHeight: mapContainer()?.clientHeight
      });
      logDebug('Using Mapbox style:', config.MAPBOX_STYLE);
      logDebug('Style check - config.MAPBOX_STYLE:', config.MAPBOX_STYLE);
      logDebug('Style check - trim result:', config.MAPBOX_STYLE?.trim());
      logDebug('Style check - condition result:', (config.MAPBOX_STYLE && config.MAPBOX_STYLE.trim() !== ''));
      
      // Use satellite style without roads for marine/sailing applications
      const customStyle = config.MAPBOX_STYLE && config.MAPBOX_STYLE.trim() !== '' ? config.MAPBOX_STYLE : 'mapbox://styles/mapbox/satellite-v9';
      const selectedStyle = customStyle;
      logDebug('Selected style:', selectedStyle);
      
      // Clear the container before initializing the map (Mapbox requires empty container)
      const container = mapContainer();
      if (container) {
        // Remove all child elements to ensure container is empty
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
      
      try {
        const newMap = new mapboxgl.Map({
          container: container,
          // Use user-provided style when available; fall back to default
          style: selectedStyle,
          center: savedState?.center || [-74.006, 40.7128], // Default to New York City
          zoom: savedState?.zoom || 14,
          bearing: 0, // Orientation from wind-arrow index + current TWD (createEffect), not from saved bearing
          pitch: 0,
          antialias: true,
          attributionControl: true
        });

        logDebug('Map created successfully');
        setMap(newMap);

        // Store map reference globally for compatibility
        window.map = newMap;

        // Ensure attribution control is visible (in case style suppressed defaults)
        try {
          newMap.addControl(new mapboxgl.AttributionControl({ compact: false }));
        } catch (e) {
          logWarn('Could not add attribution control explicitly:', e);
        }

        // Add map event listeners
        newMap.on('load', () => {
          logDebug('Map loaded successfully');
          logDebug('Map loaded successfully - tiles should be visible now');
          
          // Enable all map interactions (without visible controls)
          // Disable built-in doubleClickZoom - we use custom handler to fit bounds to data
          newMap.doubleClickZoom.disable();
          newMap.scrollZoom.enable();
          newMap.boxZoom.enable();
          newMap.dragPan.enable();
          newMap.dragRotate.enable();
          newMap.keyboard.enable();
          newMap.touchZoomRotate.enable();
          
          // Setup resize observers after map is loaded
          setupResizeObservers();
          
          // Trigger initial resize to ensure correct sizing, especially in split view
          // Use a small delay to allow DOM to settle
          setTimeout(() => {
            triggerMapResize();
          }, 100);
          
          // Don't clear loading here - wait for data to be received via handleDataUpdate
          // Loading will be cleared when initial data is received
          logDebug('MapContainer: Map initialized, waiting for data...');
          
          // After fitBounds, only clear programmatic flag. Bearing is applied by createEffect from wind-arrow index + current TWD.
          const clearProgrammaticFlag = () => setTimeout(() => setIsProgrammaticMovement(false), 100);

          // Check if we have data to center on immediately after map loads
          const currentValues = values();
          if (currentValues && currentValues.length > 0) {
            logDebug('MapContainer: Map loaded with existing data, centering immediately');
            const boundsData = getDataForBounds(currentValues);
            const dataToFit = boundsData.length > 0 ? boundsData : currentValues;
            const bounds = calculateBounds(dataToFit);
            if (bounds) {
              setIsProgrammaticMovement(true);
              fitBoundsPreservingOrientation(newMap, bounds);
              setInitialFitComplete(true);
              newMap.once('moveend', clearProgrammaticFlag);
              setTimeout(clearProgrammaticFlag, 200);
            } else {
              clearProgrammaticFlag();
            }
          } else {
            clearProgrammaticFlag();
          }
        });

        // Add double-click handler to fit bounds to all data
        newMap.on('dblclick', (e) => {
          // Stop event propagation to prevent conflicts in split view
          if (e.originalEvent) {
            e.originalEvent.stopPropagation();
            e.originalEvent.preventDefault();
          }
          
          logDebug('MapContainer: Double-click detected');
          
          // Use trackData() instead of values() to get all track data
          const data = trackData();
          logDebug('MapContainer: Double-click - trackData', {
            dataLength: data?.length || 0,
            hasData: !!(data && data.length > 0)
          });
          
          if (data && data.length > 0) {
            const bounds = calculateBounds(data);
            logDebug('MapContainer: Double-click - calculated bounds', { bounds });
            
            if (bounds) {
              logDebug('MapContainer: Double-click - fitting bounds to all track data', {
                bounds,
                padding: 50,
                maxZoom: 16
              });
              setIsProgrammaticMovement(true);
              fitBoundsPreservingOrientation(newMap, bounds, { duration: 1000 });
              setTimeout(() => setIsProgrammaticMovement(false), 1100);
            } else {
              logDebug('MapContainer: Double-click - calculateBounds returned null, cannot fit bounds');
            }
          } else {
            logDebug('MapContainer: Double-click - no track data available');
          }
        });

        newMap.on('error', (e) => {
          logError('Map error:', e);
          logError('Mapbox error details:', e);
        });

        // Track zoom level for visibility control and trigger updates
        // Always update on zoom events - SolidJS signals handle deduplication automatically
        newMap.on('zoom', () => {
          const zoom = newMap.getZoom();
          setCurrentZoom(zoom);
        });

        newMap.on('zoomend', () => {
          const zoom = newMap.getZoom();
          setCurrentZoom(zoom);
          scheduleResumeIfUserMove();
          // Log visibility state after zoom ends for debugging
          logDebug('MapContainer: Zoom ended at', zoom, {
            boatsThreshold: boatsZoomThreshold(),
            maneuversThreshold: maneuversZoomThreshold(),
            raceCourseThreshold: raceCourseZoomThreshold(),
            showBoats: zoom >= boatsZoomThreshold(),
            showManeuvers: zoom >= maneuversZoomThreshold(),
            showRaceCourse: zoom >= raceCourseZoomThreshold()
          });
        });

        // Initialize zoom level
        const initialZoom = newMap.getZoom();
        setCurrentZoom(initialZoom);

        // After style/tiles/layout settle, sync zoom again so race-course visibility (showRaceCourse) matches
        // the real map — avoids boundaries staying hidden until a toggle/remount when the signal lagged first paint.
        newMap.once("idle", () => {
          try {
            setCurrentZoom(newMap.getZoom());
          } catch {
            /* ignore */
          }
        });

        // Helper to compute whether tiles are available (any sources present)
        // Only update if value actually changed to prevent unnecessary re-renders
        let lastTilesAvailable = true;
        const computeTilesAvailability = () => {
          // Debounce to prevent rapid successive calls
          if (tilesAvailabilityTimeout) {
            clearTimeout(tilesAvailabilityTimeout);
          }
          tilesAvailabilityTimeout = setTimeout(() => {
            try {
              const styleObj = newMap.getStyle();
              if (!styleObj || !styleObj.sources) {
                if (lastTilesAvailable !== false) {
                  setTilesAvailable(false);
                  lastTilesAvailable = false;
                }
                return;
              }
              const sourceIds = Object.keys(styleObj.sources || {});
              const available = sourceIds.length > 0;
              // Only update signal if value actually changed
              if (lastTilesAvailable !== available) {
                setTilesAvailable(available);
                lastTilesAvailable = available;
                logDebug('Tiles availability check:', { available, sourceIds });
              }
            } catch (e) {
              if (lastTilesAvailable !== false) {
                setTilesAvailable(false);
                lastTilesAvailable = false;
              }
            }
          }, 100); // Debounce by 100ms
        };

        // Add style load error handler
        newMap.on('style.load', () => {
          logDebug('Map style loaded successfully');
          logDebug('Style name:', newMap.getStyle().name);
          logDebug('Style sources:', Object.keys(newMap.getStyle().sources));
          logDebug('Map center:', newMap.getCenter());
          logDebug('Map zoom:', newMap.getZoom());
          
          // Check style type and provide feedback
          const styleName = newMap.getStyle().name;
          if (styleName === 'Blank') {
            logWarn('⚠️ Blank style detected - switching to satellite style (no roads)');
            newMap.setStyle('mapbox://styles/mapbox/satellite-v9');
          } else if (styleName.includes('Satellite') || styleName.includes('satellite')) {
            logDebug('✅ Satellite style loaded - showing satellite imagery without roads');
          } else {
            logDebug('✅ Style loaded:', styleName);
          }
          
          computeTilesAvailability();
        });

        newMap.on('style.error', (e) => {
          logError('Map style error:', e);
          try {
            const el = mapContainer();
            if (el) el.style.background = '#e5e7eb'; // Tailwind gray-200
          } catch (_) { /* empty */ }
          setTilesAvailable(false);
        });

        // Add data load handler to see when tiles are loaded
        // Note: 'data' event fires very frequently - only log occasionally to avoid spam
        let dataEventCount = 0;
        newMap.on('data', () => {
          dataEventCount++;
          // Only log every 10th event to reduce console spam
          if (dataEventCount % 10 === 0) {
            logDebug('Map data loaded (event #' + dataEventCount + ')');
          }
        });

        // Only check tiles availability when a source actually finishes loading
        // This prevents the infinite loop caused by checking on every intermediate load event
        newMap.on('sourcedata', (e) => {
          // Only log and check when source finishes loading, not on every intermediate event
          if (e.isSourceLoaded) {
            logDebug('Source data loaded:', e.sourceId, e.isSourceLoaded);
            computeTilesAvailability();
          }
        });

        // Debounce resume after zoom/pan so we don't resume on every scroll tick (avoids endless loop)
        let resumeAfterMoveTimeout: ReturnType<typeof setTimeout> | null = null;
        const RESUME_DEBOUNCE_MS = 400;

        const clearResumeTimeout = () => {
          if (resumeAfterMoveTimeout != null) {
            clearTimeout(resumeAfterMoveTimeout);
            resumeAfterMoveTimeout = null;
          }
        };
        (newMap as any)._clearResumeTimeout = clearResumeTimeout;

        const scheduleResumeIfUserMove = () => {
          if (!wasPlayingBeforeMapMove() || isProgrammaticMovement()) return;
          clearResumeTimeout();
          resumeAfterMoveTimeout = setTimeout(() => {
            resumeAfterMoveTimeout = null;
            if (wasPlayingBeforeMapMove() && !isProgrammaticMovement()) {
              setWasPlayingBeforeMapMove(false);
              setIsPlaying(true);
              logDebug('MapContainer: Resuming playback after zoom/pan debounce');
            }
          }, RESUME_DEBOUNCE_MS);
        };

        // Pause playback on any user zoom or pan so animation doesn't fight with interaction
        const pausePlaybackIfUserMove = () => {
          // A zoom/pan HOLD, not a transport gesture: the clock stops so animation does not fight
          // the interaction, and `moveend` restarts it. Follow must stay armed throughout, or panning
          // a live map would silently drop the live report pages out of follow.
          if (isPlaying() && !isProgrammaticMovement()) {
            setWasPlayingBeforeMapMove(true);
            stopPlaybackClock();
            logDebug('MapContainer: Paused playback for zoom/pan');
          }
        };

        newMap.on('movestart', () => {
          setIsMapMoving(true);
          clearResumeTimeout();
          pausePlaybackIfUserMove();
        });

        newMap.on('zoomstart', () => {
          clearResumeTimeout();
          pausePlaybackIfUserMove();
        });

        newMap.on('moveend', () => {
          setIsMapMoving(false);
          scheduleResumeIfUserMove();

          // Don't overwrite saved state when we're programmatically fitting bounds (bearing would be 0)
          if (isProgrammaticMovement()) return;

          // Save map state
          const center = newMap.getCenter();
          const zoom = newMap.getZoom();
          const bearing = newMap.getBearing();
          const mapState = {
            center: [center.lng, center.lat],
            zoom: zoom,
            bearing: bearing
          };
          
          try {
            localStorage.setItem(savedStateKey, JSON.stringify(mapState));
          } catch (e) {
            logWarn('Could not save map state:', e);
          }
        });

        // Note: Double-click handler is already set up above (line ~1752)
        // Removed duplicate handler to avoid conflicts

      } catch (mapError) {
        logError('Error creating map:', mapError);
        // Clear timeout and set loading to false even if map creation fails
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        unifiedDataStore.setLoading('map', false);
      }
    } catch (error: any) {
      logError('Error in initMap:', error);
      // Clear timeout and set loading to false even if map initialization fails
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        loadingTimeout = null;
      }
      unifiedDataStore.setLoading('map', false);
    }
  };

  // Initialize time synchronization
  const initTime = () => {
    try {
      // Start periodic sync for selectedTime
      startPeriodicSync();
      
      // Set up manual sync
      syncSelectedTimeManual();
      
      logDebug('Time initialization completed');
    } catch (error: any) {
      logError('Error in initTime:', error);
    }
  };

  // Handle data updates from MapTimeSeries
  let lastHandledSignature = null; // Prevent redundant handleDataUpdate loops
  let forceNextUpdate = false; // Bypass dedupe once when needed
  const handleDataUpdate = async (payload) => {
    // MapTimeSeries now consistently sends actual data arrays with event_id already assigned
    const filteredData = Array.isArray(payload) ? payload : [];
    
    if (!Array.isArray(payload)) {
      logWarn('⚠️ MapContainer: Received non-array payload from MapTimeSeries:', payload);
      return;
    }
    
    // CRITICAL: Normalize source_id field before processing to ensure consistency
    // This ensures MultiTrackLayer can properly group tracks by source
    const normalizedData = filteredData.map(pt => {
      // Normalize source_id field name - ensure it's always 'source_id' (lowercase)
      if (pt.sourceId !== undefined && pt.source_id === undefined) {
        pt.source_id = Number(pt.sourceId);
      } else if (pt.Source_id !== undefined && pt.source_id === undefined) {
        pt.source_id = Number(pt.Source_id);
      } else if (pt.sourceID !== undefined && pt.source_id === undefined) {
        pt.source_id = Number(pt.sourceID);
      }
      // Ensure source_id is a number
      if (pt.source_id !== undefined) {
        pt.source_id = Number(pt.source_id);
      }
      return pt;
    });
    
    // Validate that all data points have source_id (for multi-mode track rendering)
    if (sourceMode === 'multi' && normalizedData.length > 0) {
      const pointsWithoutSourceId = normalizedData.filter(pt => 
        !pt.source_id && pt.sourceId === undefined && pt.Source_id === undefined && pt.sourceID === undefined
      );
      
      if (pointsWithoutSourceId.length > 0) {
        logWarn('⚠️ MapContainer: Some data points missing source_id field (multi-mode)', {
          totalPoints: normalizedData.length,
          pointsWithoutSourceId: pointsWithoutSourceId.length,
          sampleMissing: pointsWithoutSourceId[0] ? Object.keys(pointsWithoutSourceId[0]).slice(0, 10) : []
        });
        // Filter out points without source_id to prevent rendering issues
        const validData = normalizedData.filter(pt => 
          pt.source_id !== undefined || pt.sourceId !== undefined || pt.Source_id !== undefined || pt.sourceID !== undefined
        );
        
        if (validData.length !== normalizedData.length) {
          logDebug('MapContainer: Filtered out points without source_id', {
            before: normalizedData.length,
            after: validData.length
          });
        }
        
        // Use validated data for the rest of the function
        // Replace normalizedData with validated data
        normalizedData.length = 0;
        normalizedData.push(...validData);
      }
    }
    
    // Use normalizedData for the rest of the function (or filteredData if no normalization was needed)
    let dataToProcess = (normalizedData && normalizedData.length > 0) ? normalizedData : filteredData;
    
    // Debug: Check if data has event_id assignments
    let eventIdCounts = {};
    if (dataToProcess && dataToProcess.length > 0) {
      dataToProcess.forEach(point => {
        const eventId = point.event_id || 0;
        eventIdCounts[eventId] = (eventIdCounts[eventId] || 0) + 1;
      });
      
      // Only log event_id info if there are points with event_id > 0 (unusual case worth noting)
      const pointsWithEventId = dataToProcess.filter(p => p.event_id > 0);
      if (pointsWithEventId.length > 0) {
        logDebug('🔍 MapContainer: Points with event_id > 0:', pointsWithEventId.length, 'out of', dataToProcess.length);
        logDebug('🔍 MapContainer: Sample points with event_id > 0:', pointsWithEventId.slice(0, 3).map(p => ({ 
          Datetime: p.Datetime, 
          event_id: p.event_id 
        })));
      }
      
      // Extract filter options to populate global filter dropdowns (only on first data load, non-live)
      if (!hasExtractedFilters) {
        // Determine the current filter context based on selected state
        const { selectedDatasetId, selectedSourceId, selectedDate } = persistantStore;
        const datasetId = selectedDatasetId?.();
        const sourceId = selectedSourceId?.();
        const date = selectedDate?.();
        
        // Use the same logic as determineFilterContext in unifiedDataStore
        let filterContext: 'dataset' | 'day' | 'fleet' | 'source' = 'fleet';
        if (datasetId && datasetId > 0) {
          filterContext = 'dataset';
        } else if (date && date !== '' && date !== '0' && (!datasetId || datasetId <= 0)) {
          filterContext = 'day';
        } else if (sourceId && sourceId > 0 && (!datasetId || datasetId <= 0)) {
          filterContext = 'source';
        }
        
        logDebug('MapContainer: Extracting filter options with context:', filterContext);
        await extractFilterOptions(dataToProcess, filterContext);
        hasExtractedFilters = true;
      }
      
      // Check if we're showing full timeline (time window = 0)
      const currentTimeWindow = timeWindow();
      const isFullTimeline = currentTimeWindow === 0;
      
      // Center map on initial load, or when there is a cut/brush selection, or when race/leg filter changes (zoom to data)
      const hasRange = Array.isArray(selectedRange()) && selectedRange().length > 0;
      const hasCut = Array.isArray(cutEvents()) && cutEvents().length > 0;
      const hasSelection = hasCut || hasRange;
      const currentFilterSig = sourceMode === 'multi'
        ? JSON.stringify([multiSelectedRaces(), multiSelectedLegs()])
        : JSON.stringify([selectedRacesTimeseries(), selectedLegsTimeseries()]);
      const filterSignatureChanged = lastHandledFilterSignature !== undefined && currentFilterSig !== lastHandledFilterSignature;
      const dataForBounds = getDataForBounds(dataToProcess);
      const boundsData = dataForBounds.length > 0 ? dataForBounds : dataToProcess;
      
      if ((!hasExtractedFilters || hasSelection || filterSignatureChanged) && dataToProcess.length > 0) {
        const bounds = calculateBounds(boundsData);
        if (bounds && map()) {
          const reason = !hasExtractedFilters ? 'on initial data load' : hasSelection ? 'to selection (cut/brush)' : 'to filter (race/leg)';
          logDebug(`MapContainer: Centering map ${reason}`, { boundsDataLength: boundsData.length });
          // Set flag to prevent map movement handlers from interfering with playback
          setIsProgrammaticMovement(true);
          fitBoundsPreservingOrientation(map(), bounds);
          setInitialFitComplete(true);
          logDebug('Map bounds fitted to track data');
          // Reset flag after a short delay
          setTimeout(() => setIsProgrammaticMovement(false), 100);
          
          // Add a fallback centering attempt in case the first one fails
          setTimeout(() => {
            const currentMap = map();
            if (currentMap && !isInitialMapDraw) {
              const currentCenter = currentMap.getCenter();
              // Check if map is still at default coordinates (New York)
              const isAtDefaultLocation = Math.abs(currentCenter.lng - (-74.006)) < 0.1 && 
                                        Math.abs(currentCenter.lat - 40.7128) < 0.1;
              
                if (isAtDefaultLocation && boundsData.length > 0) {
                logDebug('MapContainer: Fallback centering - map still at default location');
                const fallbackBounds = calculateBounds(boundsData);
                if (fallbackBounds) {
                  setIsProgrammaticMovement(true);
                  fitBoundsPreservingOrientation(currentMap, fallbackBounds);
                  setInitialFitComplete(true);
                  setTimeout(() => setIsProgrammaticMovement(false), 100);
                }
              }
            }
          }, 1000); // 1 second delay for fallback
        }
      } else if (isFullTimeline) {
        // Don't auto-fit bounds for subsequent full timeline updates - let user control map view
      } else {
        // For time windows, fit bounds on first load using selection when present
        if (!hasExtractedFilters) {
          const bounds = calculateBounds(boundsData);
          if (bounds && map()) {
            // Set flag to prevent map movement handlers from interfering with playback
            setIsProgrammaticMovement(true);
            fitBoundsPreservingOrientation(map(), bounds);
            setInitialFitComplete(true);
            logDebug('Map bounds fitted to track data');
            // Reset flag after a short delay
            setTimeout(() => setIsProgrammaticMovement(false), 100);
          }
        }
      }
      if (dataToProcess.length > 0) {
        lastHandledFilterSignature = currentFilterSig;
      }
    }
    
    // Dedupe: avoid processing identical datasets repeatedly
    // Include event_id distribution, brush state, timeWindow, selectedTime, and source distribution in signature
    // to detect when any of these change (even if data content is the same)
    const eventIdSignature = dataToProcess && dataToProcess.length > 0
      ? Object.keys(eventIdCounts).sort().join(',')
      : '0';
    
    // Include source distribution in signature to detect when data for different sources changes
    let sourceSignature = 'src0';
    if (dataToProcess && dataToProcess.length > 0) {
      const sourceIds = new Set();
      const sourceCounts = new Map();
      for (const d of dataToProcess) {
        const sourceId = d.source_id || d.sourceId;
        if (sourceId !== undefined && sourceId !== null) {
          const sid = Number(sourceId);
          if (Number.isFinite(sid)) {
            sourceIds.add(sid);
            sourceCounts.set(sid, (sourceCounts.get(sid) || 0) + 1);
          }
        }
      }
      // Create signature from sorted source IDs and their counts
      const sortedSourceIds = Array.from(sourceIds).sort((a, b) => a - b);
      const sourceCountsStr = sortedSourceIds.map(sid => `${sid}:${sourceCounts.get(sid)}`).join(',');
      sourceSignature = sortedSourceIds.length > 0 ? `src${sortedSourceIds.join(',')}_cnt${sourceCountsStr}` : 'src0';
    }
    
    // Include brush state in signature so that brush clearing triggers update even if data is the same
    const currentRange = selectedRange();
    const currentRanges = selectedRanges();
    const hasBrushSelection = (Array.isArray(currentRanges) && currentRanges.length > 0) ||
                              (Array.isArray(currentRange) && currentRange.length > 0);
    const brushSignature = hasBrushSelection ? 'brush' : 'nobrush';
    
    // Include timeWindow and selectedTime in signature so that time window changes trigger updates
    const currentTimeWindow = timeWindow();
    const currentSelectedTime = selectedTime();
    const timeWindowSignature = `tw${currentTimeWindow}`;
    const selectedTimeSignature = currentSelectedTime ? `st${currentSelectedTime.getTime()}` : 'st0';
    const raceFilterSignature = sourceMode === 'multi'
      ? JSON.stringify([multiSelectedRaces(), multiSelectedLegs()])
      : JSON.stringify([selectedRacesTimeseries(), selectedLegsTimeseries()]);
    
    // Check for special timestamp fields that indicate forced updates (brush clear, brush selection, etc.)
    const hasClearTimestamp = dataToProcess && dataToProcess.length > 0 && dataToProcess.some(d => d._clearTimestamp);
    const hasBrushTimestamp = dataToProcess && dataToProcess.length > 0 && dataToProcess.some(d => d._brushTimestamp);
    const hasGradeRefresh = dataToProcess && dataToProcess.length > 0 && dataToProcess.some(d => d._gradeRefresh);
    const forceUpdateFlag = hasClearTimestamp ? '_clear' : hasBrushTimestamp ? '_brush' : hasGradeRefresh ? `_grade${dataToProcess.find(d => d._gradeRefresh)?._gradeRefresh}` : '';
    
    const sig = dataToProcess && dataToProcess.length > 0
      ? `${dataToProcess.length}_${dataToProcess[0]?.Datetime}_${dataToProcess[dataToProcess.length - 1]?.Datetime}_${eventIdSignature}_${brushSignature}_${timeWindowSignature}_${selectedTimeSignature}_${sourceSignature}_rf${raceFilterSignature}${forceUpdateFlag}`
      : `0_${brushSignature}_${timeWindowSignature}_${selectedTimeSignature}_${sourceSignature}_rf${raceFilterSignature}${forceUpdateFlag}`;
    
    if (!forceNextUpdate && sig === lastHandledSignature) {
      return;
    }
    // Reset force flag after bypassing once
    if (forceNextUpdate) {
      forceNextUpdate = false;
    }
    lastHandledSignature = sig;

    // Update local values for chart only if not called from effect to prevent infinite loop
    if (!isUpdatingFromEffect) {
      // Data from MapTimeSeries already has event_id assigned - use it directly
      setValues(dataToProcess);
      
      // If there is no active selection or cut, treat this as the full-range dataset
      const hasRange = Array.isArray(selectedRange()) && selectedRange().length > 0;
      const hasCut = Array.isArray(cutEvents()) && cutEvents().length > 0;
      if (!hasRange && !hasCut) {
        setBaseValues(dataToProcess);
      }
    }
    
    // Clear loading state when we receive initial data (or when data fetch completes, even if empty)
    if (!hasReceivedInitialData) {
      hasReceivedInitialData = true;
      logDebug('MapContainer: Initial data received, waiting for rendering to complete', {
        dataLength: dataToProcess.length,
        hasMap: !!map()
      });
      
      // Clear the loading timeout since data was received
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        loadingTimeout = null;
      }
      
      // Wait for simple paint to ensure content is visible before clearing loading state
      // Use simple paint detection instead of complex multi-step wait to avoid blocking
      waitForPaint(2).then(() => {
        logDebug('MapContainer: Paint complete, clearing loading state');
        unifiedDataStore.setLoading('map', false);
      }).catch((error) => {
        logError('MapContainer: Error waiting for paint, clearing loading anyway:', error);
        unifiedDataStore.setLoading('map', false);
      });
    }
  };

  // Effect to center map when data becomes available after map is loaded
  createEffect(() => {
    const currentMap = map();
    const currentValues = values();
    const dataForBounds = getDataForBounds(currentValues || []);
    const boundsData = dataForBounds.length > 0 ? dataForBounds : currentValues;
    
    if (currentMap && currentValues && currentValues.length > 0) {
      // Only center if this is the first data load (hasExtractedFilters is false)
      if (!hasExtractedFilters && boundsData && boundsData.length > 0) {
        logDebug('MapContainer: Data available, centering map on track data');
        const bounds = calculateBounds(boundsData);
        if (bounds) {
          setIsProgrammaticMovement(true);
          fitBoundsPreservingOrientation(currentMap, bounds);
          setInitialFitComplete(true);
          setTimeout(() => setIsProgrammaticMovement(false), 100);
        }
      }
    }
  });

  // Multi mode: default race selection and keyboard navigation
  // Track the last date and race options to detect changes
  let lastDate = null;
  let lastRaceOptions = null;
  createEffect(() => {
    if (sourceMode !== 'multi') return;
    
    // Use selectedDate from persistantStore as the source of truth for the current date
    // This is the persistent date that's saved and synced across the app
    const currentDate = selectedDate && selectedDate();
    const races = raceOptions();
    
    // Check if date changed or race options changed
    const dateChanged = currentDate !== lastDate;
    const raceOptionsChanged = JSON.stringify(races) !== JSON.stringify(lastRaceOptions);
    
    // If date changed, clear race selection to force re-selection when new races are loaded
    if (dateChanged && lastDate !== null) {
      logDebug('MapContainer: Date changed, clearing race selection', {
        oldDate: lastDate,
        newDate: currentDate
      });
      setMultiSelectedRaces([]);
      setRaceModeStatus("pending");
    }
    
    // Update selected race if:
    // 1. No race is selected, OR
    // 2. Date changed (new day loaded), OR
    // 3. Race options changed and current selection is not in the new options
    if (Array.isArray(races) && races.length > 0) {
      const currentSelected = multiSelectedRaces();
      const currentRace = currentSelected && currentSelected.length > 0 ? currentSelected[0] : null;
      const isCurrentRaceValid = currentRace !== null && races.some((raceOption) => isSameRace(raceOption, currentRace));
      
      if (!currentRace || dateChanged || (raceOptionsChanged && !isCurrentRaceValid)) {
        logDebug('MapContainer: Updating race selection', {
          dateChanged,
          raceOptionsChanged,
          currentDate,
          currentRace,
          isCurrentRaceValid,
          availableRaces: races,
          newSelection: races[0]
        });
        setMultiSelectedRaces([races[0]]);
        setSelectedRacesTimeseries([String(races[0])]);
        syncFleetTimeWindowForRaceSelection([String(races[0])]);
      }
    }
    
    // Update tracking variables
    lastDate = currentDate;
    lastRaceOptions = races;
  });

  // Persist selected sources whenever they change (multi mode)
  createEffect(async () => {
    if (sourceMode !== 'multi') return;
    const cls = selectedClassName && selectedClassName();
    const proj = selectedProjectId && selectedProjectId();
    const dateStr = selectedDate && selectedDate();
    const setRef = selectedSourceIds();
    if (!cls || !proj || !dateStr || !(setRef instanceof Set)) return;
    try {
      const storeKey = `${cls}_${proj}_${dateStr}`;
      const saved = (await unifiedDataStore.getObject('multi_selected_sources')) || {};
      saved[storeKey] = Array.from(setRef);
      // Store in background (non-blocking)
      unifiedDataStore.storeObject('multi_selected_sources', saved).catch(() => {
        // Ignore errors for non-critical caching
      });
    } catch (_) { /* empty */ }
  });

  onMount(() => {
    const shouldIgnoreMapHotkey = (e) => {
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return true;
      }
      if (typeof document !== 'undefined' && document.querySelector('.pagesettings-modal, .tags-explore-edit-modal, .tags-explore-settings-modal')) {
        return true;
      }
      return false;
    };

    const handler = (e) => {
      if (shouldIgnoreMapHotkey(e)) return;

      if (e.key === 'x' || e.key === 'X') {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
        e.stopPropagation();
        logDebug('MapContainer: Clearing selection (x key pressed)');
        clearSelection();
        return;
      }

      if (sourceMode !== 'multi') return;
      if (!raceOptions() || raceOptions().length === 0) return;
      const races = raceOptions();
      const current = (multiSelectedRaces() && multiSelectedRaces().length > 0) ? multiSelectedRaces()[0] : races[0];
      const idx = races.findIndex((raceOption) => isSameRace(raceOption, current));
      const resolvedIdx = idx >= 0 ? idx : 0;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        const nextIdx = Math.max(0, resolvedIdx - 1);
        setMultiSelectedRaces([races[nextIdx]]);
        setSelectedRacesTimeseries([String(races[nextIdx])]);
        syncFleetTimeWindowForRaceSelection([String(races[nextIdx])]);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        const nextIdx = Math.min(races.length - 1, resolvedIdx + 1);
        setMultiSelectedRaces([races[nextIdx]]);
        setSelectedRacesTimeseries([String(races[nextIdx])]);
        syncFleetTimeWindowForRaceSelection([String(races[nextIdx])]);
      }
    };
    window.addEventListener('keydown', handler);

    const onTagsSeekTimeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ selectedTime?: Date; requestingComponent?: string }>).detail;
      if (!detail || detail.requestingComponent !== 'tags_explore') {return;}
      const seekTime = detail.selectedTime;
      if (!(seekTime instanceof Date) || Number.isNaN(seekTime.getTime())) {return;}
      const hint = takeTagsSeekMapCenter();
      queueMicrotask(() => {
        const currentMap = map();
        if (!currentMap) {return;}
        const data = trackData();
        if (!data?.length) {return;}
        setIsProgrammaticMovement(true);
        const centered = centerMapOnTrackTime(currentMap, data, seekTime, hint);
        if (!centered) {
          setIsProgrammaticMovement(false);
          return;
        }
        setTimeout(() => setIsProgrammaticMovement(false), 1100);
      });
    };
    window.addEventListener('playbackSelectedTimeChange', onTagsSeekTimeChange);

    onCleanup(() => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('playbackSelectedTimeChange', onTagsSeekTimeChange);
    });
  });

  // REMOVED: Complex consolidated update effects - MapTimeSeries handles all reactive updates internally

  // Note: triggerUpdate is now handled by MapTimeSeries as the data controller
  // MapContainer receives data updates via onMapUpdate callback

  // Handle point click from map interactions
  const handlePointClick = (point) => {
    if (point && point.Datetime) {
      const time = new Date(point.Datetime);
      logDebug('MapContainer: Map point click - requesting time control');
      
      // Request control of selectedTime
      if (requestTimeControl('map')) {
        setIsManualTimeChange(true); // Set manual change flag for boat animation
        setSelectedTime(time, 'map');
        setStableSelectedTime(time);
        
        // Release time control after setting the time
        setTimeout(() => {
          logDebug('MapContainer: Releasing time control after point click');
          releaseTimeControl('map');
        }, 100);
      } else {
        logDebug('MapContainer: Time control denied - another component has higher priority');
      }
    }
  };

  // Handle range selection from map interactions
  const handleRangeSelect = (startPoint, endPoint) => {
    if (startPoint && endPoint) {
      const startTime = new Date(startPoint.Datetime);
      const endTime = new Date(endPoint.Datetime);
      
      const range = {
        type: "range",
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString()
      };
      
      
      setSelectedRange([range]);
      setHasSelection(true);
      setIsCut(false);
      
      // Filter data for the selected range
      const filteredData = values().filter(d => {
        const timestamp = new Date(d.Datetime);
        return timestamp >= startTime && timestamp <= endTime;
      });
      
      handleDataUpdate(filteredData);
    }
  };

  // Handle boat click: in FleetMap multi mode, click only toggles boat highlight (no time change); otherwise seek time
  const handleBoatClick = (point) => {
    if (sourceMode === 'multi' && props.onToggleBoatSelection && point) {
      const sid = point.source_id ?? point.Source_id ?? point.sourceId;
      if (sid != null && sid !== undefined) {
        props.onToggleBoatSelection(Number(sid));
        return; // only toggle highlight, do not move playhead
      }
    }
    handlePointClick(point);
  };

  // Fit bounds to selection/data while keeping current map orientation (no rotation).
  // Uses duration: 0 so the map never animates to north-up; we restore bearing/pitch immediately.
  const fitBoundsPreservingOrientation = (
    m: mapboxgl.Map,
    bounds: [[number, number], [number, number]],
    options?: { padding?: number; maxZoom?: number }
  ) => {
    const savedBearing = m.getBearing();
    const savedPitch = m.getPitch();
    m.fitBounds(bounds, { padding: 50, maxZoom: 16, ...options, duration: 0 });
    // Restore orientation next frame so the map never visibly rotates
    requestAnimationFrame(() => {
      m.setBearing(savedBearing);
      m.setPitch(savedPitch);
    });
  };

  onMount(async () => {
    logDebug('MapContainer onMount called');
    
    // Map is registered as active in createEffect above so Dashboard sees it on first render
    // Set that this page uses global filters, not its own filters
    setHasChartsWithOwnFilters(false);
    
    // Default channels are automatically loaded by defaultChannelsStore when class/project changes
    // No need to fetch here - the store handles it reactively
    
    // Initialize overlay system
    initializeOverlays();
    setOverlaysInitialized(true);
    
    // Add resize listener for responsive map sizing
    window.addEventListener('resize', handleResize);
    
    try {
      // Initialize map and time in parallel
      await Promise.all([
        initMap(),
        initTime()
      ]);
      
      logDebug('initMap completed');
      logDebug('initTime completed');
      
      // In live mode, MapContainer does NOT query IndexedDB directly
      // MultiMapTimeSeries queries IndexedDB and sends data to MapContainer via onMapUpdate
      // Historical data flow: LiveMap -> IndexedDB -> MultiMapTimeSeries -> MapContainer
      
      // Update map dimensions after map is initialized
      const container = mapContainer();
      if (container) {
        const canvasContainer = container.querySelector('.mapboxgl-canvas-container');
        if (canvasContainer) {
          const el = canvasContainer;
          setMapDimensions({
            width: el.offsetWidth || 1400,
            height: el.offsetHeight || 900
          });
        }
      }
      
      // Multi mode setup: hide selection banner by setting selectedSourceId=0 and fetch sources list (single-view only).
      // In split view, do NOT run this: the user may brush the map timeseries and we must show the selection banner.
      // Skip source fetching in live mode (sources come from streaming service)
      const inSplitViewNow = typeof document !== 'undefined' && document.querySelector('.split-view-content') != null;
      if (sourceMode === 'multi' && !inSplitViewNow) {
        try {
          // Hide selection banner for fleet multi-map, but preserve an explicit single-source pick (e.g. GER).
          const persistedSourceId = selectedSourceId();
          if (!(persistedSourceId > 0)) {
            setSelectedSourceId(0);
            try { setSelectedSourceName('ALL'); } catch (_) { /* empty */ }
          }
        } catch (_) { /* empty */ }

        // Get sources from sourcesStore (single source of truth for project sources)
        try {
          const cls = selectedClassName && selectedClassName();
          const proj = selectedProjectId && selectedProjectId();
          // Use selectedDate from persistantStore as the source of truth
          let dateStr = selectedDate && selectedDate();
          if (!dateStr && typeof window !== 'undefined' && window.datasetDate) {
            const m = String(window.datasetDate).match(/(\d{4})-(\d{2})-(\d{2})|(\d{8})/);
            if (m) {
              if (m[1]) dateStr = `${m[1]}-${m[2]}-${m[3]}`; else dateStr = String(window.datasetDate);
            }
          }
          
          if (cls && proj && dateStr) {
            // Wait for sourcesStore to be ready
            let attempts = 0;
            while (!sourcesStore.isReady() && attempts < 20) {
              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }
            
            if (sourcesStore.isReady()) {
              const projectSources = sourcesStore.sources();
              // Map to format expected by MapContainer (with dataset_id if needed)
              // Note: dataset_id will be determined when querying data for a specific date
              const sources = projectSources.map(s => ({
                source_id: s.source_id,
                source_name: s.source_name,
                dataset_id: null // Will be determined per-date when querying
              }));
              
              setAvailableSources(sources);
              logDebug('MapContainer: Loaded sources from sourcesStore', { 
                count: sources.length,
                sources: sources.map(s => ({ id: s.source_id, name: s.source_name }))
              });
              
              // Only apply API/default source selection if filterStore is still empty.
              // The multi-mode effect may have already set "all sources" (so FleetDataTable shows all boats).
              // Overwriting that with API (e.g. one saved source) would make the table jump to one source without user action.
              const alreadySet = filterStoreSelectedSources();
              if (alreadySet.length > 0) {
                logDebug('MapContainer: Skipping API source init — filterStore already has selection (e.g. all sources from multi effect)', { count: alreadySet.length });
              } else {
                try {
                  const initialSourceNames = await initializeSourceSelections();
                  const initialIds = initialSourceNames.map(name => {
                    const source = sources.find(s =>
                      String(s.source_name).toLowerCase() === String(name).toLowerCase()
                    );
                    return source ? Number(source.source_id) : null;
                  }).filter((id): id is number => id !== null);
                  if (initialIds.length > 0) {
                    setSelectedSourceIds(new Set(initialIds));
                    hasInitializedFilterStoreSources = true;
                    logDebug('MapContainer: Initialized sources from API', { count: initialIds.length, ids: initialIds });
                  }
                } catch (error) {
                  logWarn('MapContainer: Error initializing sources', error);
                  const firstSix = sources.slice(0, 6).map(s => Number(s.source_id));
                  setSelectedSourceIds(new Set(firstSix));
                  hasInitializedFilterStoreSources = true;
                  logDebug('MapContainer: Defaulted to first 6 sources (error case)', { count: firstSix.length, ids: firstSix });
                }
              }
            } else {
              logWarn('MapContainer: sourcesStore not ready after waiting');
            }
          }
        } catch (e) {
          logWarn('MapContainer: Failed to load sources from sourcesStore', e);
        }

        // Fetch races for multi-mode from /api/datasets/date/races
        try {
          const cls = selectedClassName && selectedClassName();
          const proj = selectedProjectId && selectedProjectId();
          // Use selectedDate from persistantStore as the source of truth
          let dateStr = selectedDate && selectedDate();
          if (!dateStr && typeof window !== 'undefined' && window.datasetDate) {
            const m = String(window.datasetDate).match(/(\d{4})-(\d{2})-(\d{2})|(\d{8})/);
            if (m) {
              if (m[1]) dateStr = `${m[1]}-${m[2]}-${m[3]}`; else dateStr = String(window.datasetDate);
            }
          }
          
          // Normalize date format: convert YYYYMMDD to YYYY-MM-DD if needed
          if (dateStr && dateStr.length === 8 && !dateStr.includes('-')) {
            dateStr = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
          }
          
          if (cls && proj && dateStr) {
            // Preload events for the day into agg.events so MapSettings and filters see races/legs (non-blocking)
            unifiedDataStore.preloadEventsForDate(cls, Number(proj), dateStr).catch(() => {});
            // Clear race options first to prevent using stale data
            setRaceModeStatus("pending");
            setRaceOptions([]);
            setMultiSelectedRaces([]);
            setSelectedRacesTimeseries([]);
            setIsTrainingHourMode(false);
            const timezone = await getTimezoneForDate(cls, Number(proj), dateStr);
            let racesUrl = `${apiEndpoints.app.datasets}/date/races?class_name=${encodeURIComponent(cls)}&project_id=${encodeURIComponent(proj)}&date=${encodeURIComponent(dateStr)}&from_race_events=1`;
            if (timezone) racesUrl += `&timezone=${encodeURIComponent(timezone)}`;
            logDebug('MapContainer: Fetching races from', racesUrl, 'for date:', dateStr);
            const racesResp = await getData(racesUrl);
            logDebug('MapContainer: Full races response', { success: racesResp?.success, data: racesResp?.data, raw: racesResp, date: dateStr });
            
            const racesList = (racesResp && (racesResp.data || racesResp)) || [];
            logDebug('MapContainer: Races list extracted', { count: racesList.length, sample: racesList[0], all: racesList, date: dateStr });

            const classified = classifyFleetDateRacesResponse(racesList);
            logDebug('MapContainer: Classified race fetch for date', dateStr, classified);
            applyClassifiedFleetRaces(classified);
          } else {
            logDebug('MapContainer: Cannot fetch races - missing params', { cls, proj, dateStr });
            setRaceModeStatus("none");
          }
        } catch (e) {
          logWarn('MapContainer: Failed to fetch races for multi mode', e);
          setRaceOptions([]);
          setMultiSelectedRaces([]);
          setSelectedRacesTimeseries([]);
          setIsTrainingHourMode(false);
          setRaceModeStatus("error");
        }

        // In multi mode, defer initial data rendering to MultiMapTimeSeries so race filters apply on first draw
      }

      // Map initialization complete - data will come from MapTimeSeries
      logDebug('Map ready, waiting for data from MapTimeSeries...');
    } catch (error: any) {
      logError('Error in map initialization:', error);
    }
  });

  onCleanup(() => {
    // Unregister is handled by createEffect cleanup; keep releaseTimeControl etc. here
    // Clear resume-after-zoom timeout so it doesn't fire after unmount
    const m = map();
    if (m && (m as any)._clearResumeTimeout) (m as any)._clearResumeTimeout();
    
    // Release time control when component unmounts
    logDebug('🗺️ MapContainer: Releasing time control on cleanup');
    releaseTimeControl('map');
    
    // Do NOT stop playback on unmount: when switching to split view the single map unmounts
    // before the split DOM exists, so we would always stop the timer. Let playback keep
    // running; the panel map will continue to receive updates. Playback is stopped when
    // the user pauses or when the app leaves live/map context elsewhere.
    
    // Clean up loading timeout
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
      loadingTimeout = null;
      unifiedDataStore.setLoading('map', false);
    }
    
    
    // Clean up resize listener
    window.removeEventListener('resize', handleResize);
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    
    // Clean up observers
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    if (mutationObserver) {
      mutationObserver.disconnect();
    }
    
    // Clean up periodic check
    if (window.mapPeriodicCheck) {
      clearInterval(window.mapPeriodicCheck);
      delete window.mapPeriodicCheck;
    }
    
    // Clean up tiles availability timeout
    if (tilesAvailabilityTimeout) {
      clearTimeout(tilesAvailabilityTimeout);
      tilesAvailabilityTimeout = null;
    }
    
    // Clean up abort controller
    if (abortController) {
      abortController.abort();
    }
    
    // Clean up map
    const currentMap = map();
    if (currentMap) {
      currentMap.remove();
    }
    
    // Clean up global map reference
    if (window.map) {
      delete window.map;
    }
    
    // Clean up global boat icon reference
    if (window.mapBoatIcon) {
      delete window.mapBoatIcon;
    }
    
    // Restore selectedSourceId if we forced it to 0 in multi mode
    try {
      if (sourceMode === 'multi' && typeof selectedSourceId === 'function' && selectedSourceId() === 0) {
        // No-op: let navigation choose new context; do not persist 0 beyond this map
      }
    } catch (_) { /* empty */ }

    // Reset the hasChartsWithOwnFilters flag when leaving the map page
    setHasChartsWithOwnFilters(false);
  })

  const brushInterval = (): { start: string; end: string } | null => {
    const ranges = selectedRange();
    if (!Array.isArray(ranges) || ranges.length === 0) {
      return null;
    }
    const first = ranges[0] as { start_time?: string; end_time?: string };
    if (!first?.start_time || !first?.end_time) {
      return null;
    }
    return { start: String(first.start_time), end: String(first.end_time) };
  };

  // Readers and power users do not get the menu. Write on this project means administrator,
  // publisher, or contributor (super users included).
  const gradeMenuEnabled = () =>
    !!props.exploreUserEventsUi &&
    sourceMode !== "multi" &&
    hasProjectWritePermission(user(), Number(selectedProjectId()));

  const mapContextEnabled = () =>
    hasProjectWritePermission(user(), Number(selectedProjectId()));

  const readTrackLatLng = (row: Record<string, unknown> | null | undefined) => {
    if (!row || typeof row !== "object") {
      return null;
    }
    const lat = Number(row[latName()] ?? row.Lat_dd ?? row.lat);
    const lng = Number(row[lngName()] ?? row.Lng_dd ?? row.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  };

  // Right-click on a track uses that sample. A click in open water uses the map point.
  const placeForM1 = (m: { unproject: (p: [number, number]) => { lat: number; lng: number }; project: (ll: [number, number]) => { x: number; y: number }; getCanvas: () => HTMLCanvasElement }, e: MouseEvent) => {
    const rect = m.getCanvas().getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let cursor: { lat: number; lng: number; onTrack: boolean } | null = null;
    try {
      const ll = m.unproject([x, y]);
      if (Number.isFinite(ll.lat) && Number.isFinite(ll.lng)) {
        cursor = { lat: ll.lat, lng: ll.lng, onTrack: false };
      }
    } catch (err) {
      logDebug("Explore map M1 click could not be projected", err);
    }
    const rows = trackData() || [];
    if (!cursor || rows.length === 0) {
      return cursor;
    }
    const coarse = Math.max(1, Math.floor(rows.length / 12000));
    let bestI = -1;
    let bestD = Infinity;
    const distanceAt = (index: number) => {
      const ll = readTrackLatLng(rows[index]);
      if (!ll) {
        return null;
      }
      const px = m.project([ll.lng, ll.lat]);
      return Math.hypot(px.x - x, px.y - y);
    };
    for (let i = 0; i < rows.length; i += coarse) {
      const d = distanceAt(i);
      if (d != null && d < bestD) {
        bestD = d;
        bestI = i;
      }
    }
    if (bestI < 0) {
      return cursor;
    }
    const from = Math.max(0, bestI - coarse);
    const to = Math.min(rows.length, bestI + coarse + 1);
    for (let i = from; i < to; i++) {
      const d = distanceAt(i);
      if (d != null && d < bestD) {
        bestD = d;
        bestI = i;
      }
    }
    if (bestD <= 36) {
      const ll = readTrackLatLng(rows[bestI]);
      if (ll) {
        return { lat: ll.lat, lng: ll.lng, onTrack: true };
      }
    }
    return cursor;
  };

  const resolveDayDate = async () => {
    const selected = String(selectedDate?.() ?? "").replace(/[-/]/g, "");
    if (/^\d{8}$/.test(selected)) {
      return selected;
    }
    const datasetId = persistantStore.selectedDatasetId?.();
    const className = selectedClassName?.();
    const projectId = selectedProjectId?.();
    if (!datasetId || !className || !projectId) {
      return "";
    }
    const response = await getData(
      `${apiEndpoints.app.datasets}/info?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(String(projectId))}&dataset_id=${encodeURIComponent(String(datasetId))}`
    );
    const fromDataset = String(response?.success ? response.data?.date ?? "" : "").replace(/[-/]/g, "");
    return /^\d{8}$/.test(fromDataset) ? fromDataset : "";
  };

  createEffect(() => {
    if (!mapContextEnabled()) {
      return;
    }
    const m = map();
    if (!m) {
      return;
    }
    const container = m.getContainer();
    const onCtx = (e: MouseEvent) => {
      if (gradeApplying() || m1Saving()) {
        return;
      }
      const place = placeForM1(m, e);
      const canGrade = gradeMenuEnabled() && !!brushInterval();
      if (!place && !canGrade) {
        return;
      }
      e.preventDefault();
      setTooltip({ visible: false, content: "", x: 0, y: 0 });
      setGradeMenuOpen(false);
      const menuWidth = 300;
      setGradeMenu({
        x: e.clientX,
        y: e.clientY,
        flip: e.clientX + menuWidth > window.innerWidth,
        lat: place ? place.lat : null,
        lng: place ? place.lng : null,
        onTrack: !!place?.onTrack,
      });
    };
    // Capture so a right-click on a track overlay still opens the menu.
    container.addEventListener("contextmenu", onCtx, true);
    const dismiss = () => setGradeMenu(null);
    m.on("move", dismiss);
    onCleanup(() => {
      container.removeEventListener("contextmenu", onCtx, true);
      try {
        m.off("move", dismiss);
      } catch (_) {
        /* map already removed */
      }
    });
  });

  createEffect(() => {
    if (!gradeMenu()) {
      return;
    }
    const onDoc = (ev: MouseEvent) => {
      const target = ev.target;
      if (!(target instanceof Node)) {
        return;
      }
      const menu = document.querySelector(".explore-map-ctx-menu");
      if (menu && menu.contains(target)) {
        return;
      }
      setGradeMenu(null);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        setGradeMenu(null);
      }
    };
    document.addEventListener("click", onDoc, true);
    document.addEventListener("keydown", onKey);
    onCleanup(() => {
      document.removeEventListener("click", onDoc, true);
      document.removeEventListener("keydown", onKey);
    });
  });

  const submitM1 = async () => {
    const menu = gradeMenu();
    if (!menu || menu.lat == null || menu.lng == null || m1Saving()) {
      return;
    }
    const className = selectedClassName?.();
    const projectId = selectedProjectId?.();
    setGradeMenu(null);
    if (!className || !projectId) {
      toastStore.showToast("error", "M1 not saved", "Select a project first.");
      return;
    }
    setM1Saving(true);
    try {
      const dateStr = await resolveDayDate();
      if (!dateStr) {
        toastStore.showToast("error", "M1 not saved", "Select a day or a dataset first.");
        return;
      }
      const response = await postData(`${apiEndpoints.app.projects}/object`, {
        class_name: className,
        project_id: Number(projectId),
        date: dateStr,
        object_name: "prestart_m1",
        json: JSON.stringify({ NAME: "M1", LAT: menu.lat, LON: menu.lng }),
      });
      if (!response?.success) {
        toastStore.showToast("error", "M1 not saved", response?.message || "The day object was not saved.");
        logError("Explore map M1 save failed", response);
        return;
      }
      logDebug("Explore map saved prestart M1", { date: dateStr, lat: menu.lat, lng: menu.lng, onTrack: menu.onTrack });
      toastStore.showToast(
        "success",
        "M1 saved",
        "Saved for this day. Re-run the Race step so the prestart map uses it as 0,0."
      );
    } catch (err) {
      logError("Explore map M1 save failed", err);
      toastStore.showToast("error", "M1 not saved", err instanceof Error ? err.message : String(err));
    } finally {
      setM1Saving(false);
    }
  };

  const submitGrade = async (grade: number) => {
    const interval = brushInterval();
    if (!interval || gradeApplying()) {
      return;
    }
    setGradeMenu(null);
    setGradeApplying(true);
    setGradeJob(null);
    try {
      await applyMapGrade({
        grade,
        interval,
        onJob: (scriptName, handle) => {
          setGradeJob({
            jobId: handle.jobId,
            processId: handle.processId,
            label: scriptName === "0_map.py" ? "Updating map…" : "Writing grade into processed data…",
          });
        },
      });
      window.dispatchEvent(new CustomEvent("racesight-map-grade-applied"));
      toastStore.showToast("success", `Grade ${grade} applied`, "The map has been rebuilt from the updated data.");
      logDebug("[MapContainer] manual grade applied", { grade, interval });
    } catch (err) {
      logError("[MapContainer] manual grade failed", err);
      toastStore.showToast("error", "Grade not applied", err instanceof Error ? err.message : String(err));
    } finally {
      setGradeApplying(false);
      setGradeJob(null);
    }
  };

  return (
    <Show when={hasShownMapOnce() || (isSourcesReady() && (sourceMode === 'multi' || availableSources().length > 0))} fallback={<Loading message="Initializing map..." />}>
    <div class="map-container" style={`display: flex; flex-direction: column; width: 100%; height: 100%; position: relative;`}>
      <MapSettings
        useIconTrigger={true}
        hideAllFilters={false}
        hideColorOptions={sourceMode === 'multi'}
        hideOverlayOptionsLabel={false}
        showRaceAllOption={sourceMode === 'single'}
        showHoverRacePills={true}
        useUnfilteredOptions={true}
        filterConfig={mapFilterScope === 'raceLegOnly' ? { showGrades: false, showTWA: false, showRaces: true, showLegs: true } : undefined}
        colorOptions={maptypes()}
        dataSourcesOptions={dataSourcesOptions()}
        displayOptions={[
          // Overlay toggles - connected to overlay registry (Data Overlay is in separate combo below)
          { key: 'overlay-tracks', label: 'Tracks', type: 'toggle', signal: [tracksEnabled, setTracksEnabled], values: [true] },
          { key: 'overlay-maneuvers', label: 'Maneuvers', type: 'toggle', signal: [maneuversEnabled, setManeuversEnabled], values: [true] },
          { key: 'overlay-boundaries', label: 'Marks & Boundaries', type: 'toggle', signal: [boundariesEnabled, setBoundariesEnabled], values: [true] },
          { key: 'overlay-bad-air', label: 'Bad Air', type: 'toggle', signal: [badAirEnabled, setBadAirEnabled], values: [true] },
          { key: 'overlay-mark-wind', label: 'Mark Wind', type: 'toggle', signal: [markWindEnabled, setMarkWindEnabled], values: [true] },
          { key: 'overlay-boat-wind', label: 'Boat Wind', type: 'toggle', signal: [boatWindEnabled, setBoatWindEnabled], values: [true] },
        ]}
        selectedDataOverlay={effectiveDataOverlay}
        onDataOverlayChange={setDataOverlayName}
        referenceBoatOptions={sourceMode === 'multi' ? referenceBoatOptions() : undefined}
        selectedReferenceBoat={sourceMode === 'multi' ? referenceBoatName() : undefined}
        onReferenceBoatChange={sourceMode === 'multi' ? setReferenceBoatName : undefined}
        isFleetMap={sourceMode === 'multi'}
        dataOverlayParentName={sourceMode === 'multi' ? 'fleet_map' : 'overlay'}
        options={twaFilterOptions()}
        selectedStatesTimeseries={selectedStatesTimeseries()}
        setSelectedStatesTimeseries={setSelectedStatesTimeseries}
        raceOptions={raceOptions()}
        setRaceOptions={setRaceOptions}
        legOptions={legOptions()}
        setLegOptions={setLegOptions}
        gradeOptions={gradeOptions()}
        setGradeOptions={setGradeOptions}
        selectedRacesTimeseries={sourceMode === 'multi' ? multiSelectedRaces() : selectedRacesTimeseries()}
        setSelectedRacesTimeseries={setNormalizedSelectedRaces}
        selectedRaces={sourceMode === 'multi' ? multiSelectedRaces() : selectedRacesTimeseries()}
        setSelectedRaces={setNormalizedSelectedRaces}
        selectedLegsTimeseries={sourceMode === 'multi' ? multiSelectedLegs() : selectedLegsTimeseries()}
        setSelectedLegsTimeseries={sourceMode === 'multi' ? setMultiSelectedLegs : setSelectedLegsTimeseries}
        selectedLegs={sourceMode === 'multi' ? multiSelectedLegs() : selectedLegsTimeseries()}
        selectedGradesTimeseries={selectedGradesTimeseries()}
        toggleFilter={(groupIndex, chartIndex, filter) => {
          const currentFilters = selectedStatesTimeseries();
          let newFilters;
          if (currentFilters.includes(filter)) {
            newFilters = currentFilters.filter(f => f !== filter);
          } else {
            newFilters = [...currentFilters, filter];
          }
          setSelectedStatesTimeseries(newFilters);
        }}
        toggleRaceFilter={(race) => {
          const normalizedRace = String(race);
          if (sourceMode === 'multi') {
            setMultiSelectedRaces([normalizedRace]);
            setSelectedRacesTimeseries([normalizedRace]);
            syncFleetTimeWindowForRaceSelection([normalizedRace]);
          } else {
            const currentRaces = normalizeRaceSelection(selectedRacesTimeseries());
            let newRaces;
            if (currentRaces.some((r) => isSameRace(r, normalizedRace))) {
              newRaces = currentRaces.filter((r) => !isSameRace(r, normalizedRace));
            } else {
              newRaces = [...currentRaces, normalizedRace];
            }
            setSelectedRacesTimeseries(newRaces);
          }
        }}
        toggleLegFilter={(leg) => {
          // Legs are multi-select: toggle in/out. Normalize to string for store consistency.
          const legStr = typeof leg === 'number' ? String(leg) : leg;
          if (sourceMode === 'multi') {
            const current = multiSelectedLegs();
            const exists = current.includes(legStr);
            const next = exists ? current.filter((l) => l !== legStr) : [...current, legStr];
            setMultiSelectedLegs(next);
          } else {
            const currentLegs = selectedLegsTimeseries();
            const exists = currentLegs.includes(legStr);
            const newLegs = exists ? currentLegs.filter((l) => l !== legStr) : [...currentLegs, legStr];
            setSelectedLegsTimeseries(newLegs);
          }
        }}
        toggleGradeFilter={(grade) => {
          const currentGrades = selectedGradesTimeseries();
          let newGrades;
          if (currentGrades.includes(grade)) {
            newGrades = currentGrades.filter(g => g !== grade);
          } else {
            newGrades = [...currentGrades, grade];
          }
          setSelectedGradesTimeseries(newGrades);
        }}
        builderRoute="/overlay-builder"
      />

      {/* Map area: wrapper so overlay controls are not removed when Mapbox clears its container */}
      <div 
        class={`map${unifiedDataStore.getLoading('map') ? " map-loading-extend" : ""}`}
        style="flex: 1; width: 100%; position: relative; min-height: 400px;"
        onMouseEnter={() => setIsHoveringMap(true)}
        onMouseMove={() => setIsHoveringMap(true)}
        onMouseLeave={() => setIsHoveringMap(false)}
      >
        {/* Mapbox target: this div is cleared on map init (all children removed); keep nothing else here */}
        <div 
          ref={(el) => {
            setMapContainer(el);
            logDebug('🗺️ Map container ref callback called', { 
              hasElement: !!el, 
              width: el?.offsetWidth, 
              height: el?.offsetHeight,
              sourceMode 
            });
            try {
              if (el) el.style.background = 'var(--color-bg-primary)'; // Use theme-aware background
            } catch (_) { /* empty */ }
          }}
          style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%;"
        />
        {/* Overlay: not cleared by Mapbox; stays on top for loading. PlayPause is in the bar above the timeline. */}
        <div class="map-controls-overlay">
          <Show when={unifiedDataStore.getLoading('map')}>
            <Loading message="Loading map data..." />
          </Show>
        </div>
        {/* Debug overlay disabled to reduce console noise during playback */}
        {false && logDebug('🗺️ MapContainer render state:', {
          hasMap: !!map(), 
          valuesLength: values().length,
          sourceMode,
          selectedSourceIdsSize: selectedSourceIds().size,
          multiRaces: multiSelectedRaces(),
          tilesAvailable: tilesAvailable()
        })}
      </div>
      
      {/* Debug loading state - disabled to reduce console noise during playback */}
      {false && logDebug('MapContainer render - loading state:', unifiedDataStore.getLoading('map'))}

      {/* Data Overlay (from MapSettings combo or props when opened from sidebar) - single-source map only; hidden in split view. when=name so keyed callback receives the overlay name; remounts when name changes. */}
      <Show when={sourceMode !== 'multi' && !isInSplitView() && effectiveDataOverlay() ? effectiveDataOverlay() : undefined} keyed>
        {(overlayName) => (
          <Overlay 
            objectName={typeof overlayName === 'function' ? overlayName() : overlayName}
            onDataUpdate={handleDataUpdate}
          />
        )}
      </Show>

      {/* Fleet Data Table overlay - multi (Fleet Map) only; hidden in split view. when=name so keyed callback receives the overlay name; remounts when name changes. */}
      <Show when={sourceMode === 'multi' && !isInSplitView() && effectiveDataOverlay() ? effectiveDataOverlay() : undefined} keyed>
        {(overlayName) => (
          <Suspense fallback={null}>
            <FleetDataTable
              selectedSourceIds={selectedSourceIds}
              objectName={typeof overlayName === 'function' ? overlayName() : overlayName}
              cachedMapData={values}
            />
          </Suspense>
        )}
      </Show>

      {/* Timeline section: chart in its own container; play/pause overlay as sibling so chart SVG rules don't affect control icons */}
      <div
        class="map-timeline-section map-timeline-section-compact"
        style="flex-shrink: 0; width: 100%; display: flex; flex-direction: column; border-top: 1px solid var(--color-border-primary); position: relative; z-index: 20;"
        onMouseEnter={() => setIsHoveringTimeline(true)}
        onMouseLeave={() => setIsHoveringTimeline(false)}
      >
        <div
          ref={(el) => (chartContainer = el)}
          class="chart-container chart-container-compact map-timeline-chart-wrap"
          style="flex-shrink: 0; width: 100%; height: 120px; background: var(--color-bg-card); position: relative;"
        >
          <Show when={sourceMode === 'multi'} fallback={
            <MapTimeSeries
              maptype={maptype()}
              samplingFrequency={samplingFrequency()}
              onMapUpdate={handleDataUpdate}
              onStableSelectedTimeChange={setStableSelectedTime}
              mapFilterScope={mapFilterScope}
            />
          }>
            <MultiMapTimeSeries
              maptype={sourceMode === 'multi' ? 'DEFAULT' : maptype()}
              samplingFrequency={samplingFrequency()}
              onMapUpdate={handleDataUpdate}
              onStableSelectedTimeChange={setStableSelectedTime}
              cachedData={values() || []}
              selectedSourceIds={selectedSourceIds}
              selectedRacesTimeseries={multiSelectedRaces()}
              selectedLegsTimeseries={multiSelectedLegs()}
              highlightedSourceIds={highlightedSourceIds()}
              referenceSourceId={referenceSourceId}
            />
          </Show>
        </div>
        <div class={`timeline-controls-overlay${isHoveringTimeline() ? " timeline-controls-overlay-visible" : ""}`}>
          <PlayPause position="timeline-overlay" allowFastFwd={true} allowTimeWindow={true} hideFullTimeWindow={sourceMode === 'multi' && shouldHideFleetFullTimeWindowForMode(raceModeStatus(), isTrainingHourMode(), raceOptions(), effectiveSelectedRaces())} />
        </div>
      </div>

      {/* New Component Architecture */}
      {/* In live mode, show map even if no data yet (data will stream in) - debug log disabled to reduce playback noise */}
      <Show when={map() && values().length > 0 && tracksEnabled()}>
        {sourceMode === 'multi' ? (
          <>
            <Show when={multiModeLayersKey()} keyed>
              {(_) => (
                <>
                  <Show
                    when={nativeMode()}
                    fallback={
                      <MultiTrackLayer
                        data={trackData()}
                        map={map()}
                        maptype={maptype()}
                        samplingFrequency={samplingFrequency()}
                        tilesAvailable={tilesAvailable()}
                        maneuversEnabled={maneuversEnabledComputed()}
                        onPointClick={handlePointClick}
                        onRangeSelect={handleRangeSelect}
                        selectedSourceIds={selectedSourceIds()}
                        enableWebSocketUpdates={false}
                        highlightedSourceIds={highlightedSourceIds()}
                        effectivePlaybackTime={effectivePlaybackTime()}
                      />
                    }
                  >
                    <GlMapMultiTrackLayer
                      data={trackData()}
                      map={map()}
                      maptype={maptype()}
                      samplingFrequency={samplingFrequency()}
                      selectedSourceIds={selectedSourceIds()}
                      highlightedSourceIds={highlightedSourceIds()}
                      effectivePlaybackTime={effectivePlaybackTime()}
                    />
                    <GlManeuverLayer
                      map={map()}
                      data={trackData()}
                      enabled={maneuversEnabledComputed()}
                      tilesAvailable={tilesAvailable()}
                      effectivePlaybackTime={effectivePlaybackTime()}
                    />
                  </Show>
                  <Show when={map() && mapContainer() && showBoats()}>
                    <Show
                      when={nativeMode()}
                      fallback={
                        <MultiBoatLayer
                          key={`multi-boat-${showBoats()}-${Array.from(selectedSourceIds()).sort().join(',')}`}
                          data={trackData()}
                          map={map()}
                          mapContainer={mapContainer()}
                          samplingFrequency={samplingFrequency()}
                          onBoatClick={handleBoatClick}
                          selectedSourceIds={selectedSourceIds}
                          hoveredSourceId={hoveredSourceId()}
                          onBoatHover={setHoveredSourceId}
                          highlightedSourceIds={highlightedSourceIds()}
                          boatWindBySourceId={boatWindBySourceId()}
                        />
                      }
                    >
                      <GlMapBoatLayer
                        data={trackData()}
                        map={map()}
                        samplingFrequency={samplingFrequency()}
                        onBoatClick={handleBoatClick}
                        selectedSourceIds={selectedSourceIds()}
                        onBoatHover={setHoveredSourceId}
                        highlightedSourceIds={highlightedSourceIds()}
                        effectivePlaybackTime={effectivePlaybackTime()}
                        boatWindBySourceId={boatWindBySourceId()}
                      />
                    </Show>
                  </Show>
                </>
              )}
            </Show>
          </>
        ) : (
          <>
            {/* Only render TrackLayer when sourcesStore is ready to prevent color flickering */}
            <Show when={tracksEnabled() && sourcesStore.isReady()}>
              <Show
                when={nativeMode()}
                fallback={
                  <TrackLayer
                    key={`track-${maneuversEnabledComputed()}-${showManeuvers()}`}
                    data={trackData()}
                    map={map()}
                    maptype={maptype()}
                    samplingFrequency={samplingFrequency()}
                    tilesAvailable={tilesAvailable()}
                    onPointClick={handlePointClick}
                    onRangeSelect={handleRangeSelect}
                    showGaps={true}
                    maneuversEnabled={maneuversEnabledComputed()}
                    zoomLevel={currentZoom()}
                    effectivePlaybackTime={effectivePlaybackTime()}
                  />
                }
              >
                <GlMapTrackLayer
                  data={trackData()}
                  map={map()}
                  maptype={maptype()}
                  samplingFrequency={samplingFrequency()}
                  onPointClick={handlePointClick}
                  showGaps={true}
                  maneuversEnabled={maneuversEnabledComputed()}
                  zoomLevel={currentZoom()}
                  effectivePlaybackTime={effectivePlaybackTime()}
                  onTrackPainted={() => setGlTrackPainted(true)}
                />
                <GlManeuverLayer
                  map={map()}
                  data={trackData()}
                  enabled={maneuversEnabledComputed()}
                  tilesAvailable={tilesAvailable()}
                  effectivePlaybackTime={effectivePlaybackTime()}
                  ready={glTrackPainted()}
                />
              </Show>
            </Show>
            <Show when={map() && mapContainer() && showBoats()}>
              <Show
                when={nativeMode()}
                fallback={
                  <BoatLayer
                    key={`boat-${showBoats()}`}
                    data={trackData()}
                    map={map()}
                    mapContainer={mapContainer()}
                    samplingFrequency={samplingFrequency()}
                    onBoatClick={handleBoatClick}
                    boatWind={boatWindBySourceId().get(Number(persistantStore.selectedSourceId?.()))}
                  />
                }
              >
                <GlMapBoatLayer
                  data={trackData()}
                  map={map()}
                  samplingFrequency={samplingFrequency()}
                  onBoatClick={handleBoatClick}
                  fallbackSourceId={persistantStore.selectedSourceId?.()}
                  effectivePlaybackTime={effectivePlaybackTime()}
                  boatWindBySourceId={boatWindBySourceId()}
                />
              </Show>
            </Show>
          </>
        )}

        {/* Selection Layer - Handles click-between-points selection */}
        <SelectionLayer
          data={trackData()}
          map={map()}
          onRangeSelect={handleRangeSelect}
          onPointClick={handlePointClick}
          showSelectionFeedback={true}
        />

        <TagsExploreMarkersLayer
          map={map()}
          trackData={trackData()}
          enabled={!!props.exploreUserEventsUi && !!persistantStore.tagsExploreRailOpen?.()}
        />
      </Show>

      {/* Race Course Layer - Renders boundaries and marks */}
      {/* Use key to force re-mount when boundariesEnabled or context changes; do NOT include persistentSettingsLoaded
          so we don't remount when settings load from API (which would kill the layer mid-init and require toggle to see it) */}
      {/* Only mount when map, boundariesEnabled, and required parameters are available */}
      {/* In single mode: requires className, projectId, and (datasetId OR selectedDate) */}
      {/* In multi mode: datasetId is 0, so RaceCourseLayer will find the correct dataset_id by date */}
      {/* In live mode: allow mounting even without datasetId or selectedDate (will use today's date as fallback) */}
      {/* RaceCourseLayer - keep mounted, control visibility via prop */}
      {map() && 
       boundariesEnabled() && 
       persistantStore.selectedClassName?.() && 
       persistantStore.selectedProjectId?.() !== undefined && 
       persistantStore.selectedProjectId?.() !== null &&
       (persistantStore.selectedDatasetId?.() || persistantStore.selectedDate?.()) && (
        <>
          <RaceCourseLayer 
            key={`race-course-${boundariesEnabled()}-${persistantStore.selectedClassName?.() || ''}-${persistantStore.selectedProjectId?.() || 0}-${persistantStore.selectedDatasetId?.() || 0}-${(persistantStore.selectedDate?.() || '').toString().replace(/[-/]/g, '')}`} 
            map={map()}
            visible={true}
          />
        </>
      )}

      {/* Alarm markers - drawn whenever an Alarms explore page is mounted, on the dataset explore
          map AND the fleet day map (both render this component). Deliberately OUTSIDE the
          tracksEnabled() Show above that wraps TagsExploreMarkersLayer: turning the Tracks overlay
          off should not silently hide alarms. The "Show on map" checkbox on the page is the other
          gate - when it is off the page publishes an empty marker array. */}
      <Show when={map()}>
        <AlarmsExploreMarkersLayer
          map={map()}
          trackData={trackData()}
          enabled={!!persistantStore.alarmsExploreOpen?.()}
          selectedSources={alarmMarkerSourceNames()}
        />
      </Show>

      {/* Wind Arrow - Shows wind direction and speed */}
      <Show when={map()}>
        <WindArrow
          map={map()}
          tws={windData().tws}
          twd={windData().twd}
          maptype={sourceMode === 'multi' ? 'DEFAULT' : maptype()}
          trackData={trackData()}
          selectedTime={selectedTime()}
          objectName={objectName}
        />
      </Show>

      {/* Overlay Manager - Manages all overlay layers (enabled in both live and non-live mode) */}
      <Show when={map() && overlaysInitialized() && mapContainer()}>
        <OverlayManager
          map={map()}
          mapContainer={mapContainer()}
          data={trackData() || []}
          width={mapDimensions.width}
          height={mapDimensions.height}
          effectivePlaybackTime={effectivePlaybackTime()}
          samplingFrequency={samplingFrequency()}
          boatWindSamples={boatWindSamples()}
          boatWindHistory={boatWindHistory()}
          enabledStates={{
            'bad-air': { get: badAirEnabled, set: setBadAirEnabled },
            'mark-wind': { get: markWindEnabled, set: setMarkWindEnabled },
          }}
        />
      </Show>

      <Show when={gradeMenu()}>
        {(menu) => (
          <Portal>
          <ul
            class="explore-map-ctx-menu"
            style={{ left: `${menu().x}px`, top: `${menu().y}px` }}
            role="menu"
          >
            <Show when={menu().lat != null && menu().lng != null}>
              <li>
                <button
                  type="button"
                  class="explore-map-ctx-menu-item"
                  role="menuitem"
                  onClick={() => void submitM1()}
                >
                  <span class="explore-map-ctx-menu-label">Add M1</span>
                </button>
              </li>
            </Show>
            <Show when={gradeMenuEnabled() && brushInterval()}>
            <li
              class="explore-map-ctx-menu-row"
              onMouseEnter={() => setGradeMenuOpen(true)}
            >
              <button type="button" class="explore-map-ctx-menu-item" role="menuitem">
                <span class="explore-map-ctx-menu-label">Set grade</span>
                <span class="explore-map-ctx-menu-caret" aria-hidden="true">›</span>
              </button>
              <Show when={gradeMenuOpen()}>
                <ul
                  class={`explore-map-ctx-menu explore-map-ctx-submenu${menu().flip ? " explore-map-ctx-submenu--flip" : ""}`}
                  role="menu"
                >
                  <For each={ASSIGNABLE_GRADES}>
                    {(grade) => (
                      <li>
                        <button
                          type="button"
                          class="explore-map-ctx-menu-item"
                          role="menuitem"
                          onClick={() => void submitGrade(grade)}
                        >
                          <span class={`explore-map-ctx-menu-grade-swatch explore-map-ctx-grade-${grade}`} aria-hidden="true" />
                          <span class="explore-map-ctx-menu-label">{`Grade ${grade}`}</span>
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </li>
            </Show>
          </ul>
          </Portal>
        )}
      </Show>
      <WaitingModal
        visible={gradeApplying()}
        process_id={gradeJob()?.processId ?? null}
        job_id={gradeJob()?.jobId ?? null}
        title="Applying grade"
        customStatus={gradeJob()?.label ?? "Saving grade…"}
        disableAutoNavigation={true}
        closable={false}
      />

      {/* Tooltip - Positioned relative to viewport */}
      <div
        id="tt"
        class="tooltip map-tooltip"
        style={{
          position: 'fixed',
          opacity: tooltip().visible ? 1 : 0,
          left: `${tooltip().x + 10}px`,
          top: `${tooltip().y - 10}px`,
          pointerEvents: 'none',
          zIndex: 9999
        }}
        innerHTML={sanitizeTooltipHtml(tooltip().content)}
      ></div>
    </div>
    </Show>
  );
}